import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo, type ReactNode } from "react";
import { ChevronDown, ChevronUp, ExternalLink, Search, Check, X, Eye, EyeOff, Edit2, Inbox, RefreshCw, Sparkles, Loader2, Trash2, Mail, BarChart3, TrendingUp, BookOpen, Clock, AlertTriangle, Calendar, LogOut, ThumbsUp, ThumbsDown, Link2, MapPin, Star } from "lucide-react";
import { toast } from "sonner";
import { refreshCandidates, approveCandidates, rejectCandidates, reviseCandidateWithAI, reviseEventWithAI } from "@/lib/scrape.functions";
import { discoverFromGooglePlaces } from "@/lib/google-places.functions";
import { runFullSeed, getSeedContext, auditTierCoverage, type SeedTier } from "@/lib/seed-destinations.functions";
import {
  recalibrateEventScores,
  fetchScoreProposals,
  decideScoreProposals,
  backfillScheduleNotes,
  fetchScheduleNotesProposals,
  decideScheduleNotesProposals,
} from "@/lib/proposals.functions";
import { verifyAdminToken } from "@/lib/admin-auth.functions";
import {
  adminFetchEvents,
  adminFetchCandidates,
  adminFetchEmailLog,
  adminFetchSuppressed,
  adminAddSuppression,
  adminFetchWizardResponses,
  adminFetchUserStats,
  adminFetchAll as adminFetchAllFn,
  adminUpdateEvent,
  adminDeleteEvents,
  adminBulkSetCurated,
  adminDeleteFeedback,
} from "@/lib/admin-data.functions";
import { adminMarkFeedbackRead } from "@/lib/admin-data.functions";
import { useServerFn } from "@tanstack/react-start";
import { getEventType, getEventMetro, METRO_LABELS, type EventType, type EventMetro } from "@/lib/event-classification";

const ADMIN_TOKEN_KEY = "rp_admin_token";

export const Route = createFileRoute("/admin/events")({
  head: () => ({
    meta: [
      { title: "Admin — Events Database — RanchPlanner" },
      { name: "description", content: "Internal admin console for managing the RanchPlanner events database, candidate scraping, and email queue." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminEventsGated,
});

function AdminEventsGated() {
  return (
    <AdminAuthGate>
      <AdminEventsPage />
    </AdminAuthGate>
  );
}

function AdminAuthGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const verifyFn = useServerFn(verifyAdminToken);
  const [status, setStatus] = useState<"checking" | "ok">("checking");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = typeof window !== "undefined" ? localStorage.getItem(ADMIN_TOKEN_KEY) : null;
      if (!token) {
        navigate({ to: "/admin/login" });
        return;
      }
      try {
        const res = await verifyFn({ data: { token } });
        if (cancelled) return;
        if (res.valid) {
          setStatus("ok");
        } else {
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          navigate({ to: "/admin/login" });
        }
      } catch {
        if (!cancelled) {
          navigate({ to: "/admin/login" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, verifyFn]);

  // Global guard: if any admin server fn throws Unauthorized (expired token),
  // clear the token and bounce back to login instead of crashing the page.
  useEffect(() => {
    function isUnauthorized(msg: unknown): boolean {
      return typeof msg === "string" && /unauthorized/i.test(msg);
    }
    function handleRejection(e: PromiseRejectionEvent) {
      const reason: any = e.reason;
      const msg = reason?.message ?? reason;
      if (isUnauthorized(msg)) {
        e.preventDefault();
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate({ to: "/admin/login" });
      }
    }
    function handleError(e: ErrorEvent) {
      if (isUnauthorized(e.error?.message ?? e.message)) {
        e.preventDefault();
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate({ to: "/admin/login" });
      }
    }
    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("error", handleError);
    return () => {
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("error", handleError);
    };
  }, [navigate]);

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <>{children}</>;
}

function AdminSignOutButton() {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate({ to: "/admin/login" });
      }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-muted text-muted-foreground hover:text-foreground transition"
      title="Sign out of admin"
    >
      <LogOut className="h-3.5 w-3.5" /> Sign out
    </button>
  );
}

// --- Safe date formatter for date ranges ---
function formatDateBubble(startDate: string | null | undefined, endDate?: string | null | undefined): string | null {
  if (!startDate) return null;
  const formatSingle = (ds: string): string => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(ds)) {
      const d = new Date(ds + "T00:00:00");
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      }
    }
    return ds;
  };
  const startFormatted = formatSingle(startDate);
  if (endDate && endDate !== startDate) {
    const endFormatted = /^\d{4}-\d{2}-\d{2}$/.test(endDate)
      ? new Date(endDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : endDate;
    return `${startFormatted} – ${endFormatted}`;
  }
  return startFormatted;
}

// --- Schedule display helper ---
function formatSchedule(event: { start_date: string | null; end_date: string | null; schedule_notes: string }): string {
  const dateLabel = formatDateBubble(event.start_date, event.end_date);
  if (dateLabel) return dateLabel;
  if (event.schedule_notes) return event.schedule_notes.slice(0, 40);
  return "Anytime";
}

// --- AI Revision Input Component (for candidates) ---
function CandidateRevisionInput({ candidateId, onRevised }: { candidateId: string; onRevised: (data: any) => void }) {
  const [guidance, setGuidance] = useState("");
  const [revising, setRevising] = useState(false);
  const reviseFn = useServerFn(reviseCandidateWithAI);

  const handleRevise = async () => {
    if (!guidance.trim()) return;
    setRevising(true);
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem(ADMIN_TOKEN_KEY) || "" : "";
      const result = await reviseFn({ data: { candidateId, guidance: guidance.trim(), token } });
      if (result.success && result.data) {
        onRevised(result.data);
        setGuidance("");
        toast.success("Listing revised by AI");
      } else {
        toast.error(result.error || "Revision failed");
      }
    } catch (err: any) {
      toast.error(err.message || "Revision failed");
    } finally {
      setRevising(false);
    }
  };

  return (
    <div className="flex gap-2 mt-2">
      <input
        type="text"
        value={guidance}
        onChange={(e) => setGuidance(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleRevise()}
        placeholder="AI revision guidance (e.g. 'fix the venue address')"
        className="flex-1 text-xs px-2.5 py-1.5 rounded-lg border border-input bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        disabled={revising}
      />
      <button
        onClick={handleRevise}
        disabled={revising || !guidance.trim()}
        className="text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1"
      >
        {revising ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
        Revise
      </button>
    </div>
  );
}

// --- AI Revision Input Component (for approved events) ---
function EventRevisionInput({ eventId, onRevised }: { eventId: string; onRevised: (changedFields: any) => void }) {
  const [guidance, setGuidance] = useState("");
  const [revising, setRevising] = useState(false);
  const reviseFn = useServerFn(reviseEventWithAI);

  const handleRevise = async () => {
    if (!guidance.trim()) return;
    setRevising(true);
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem(ADMIN_TOKEN_KEY) || "" : "";
      const result = await reviseFn({ data: { eventId, guidance: guidance.trim(), token } });
      if (result.success && result.changedFields) {
        onRevised(result.changedFields);
        setGuidance("");
        toast.success("Event revised by AI");
      } else {
        toast.error(result.error || "Revision failed");
      }
    } catch (err: any) {
      toast.error(err.message || "Revision failed");
    } finally {
      setRevising(false);
    }
  };

  return (
    <div className="flex gap-2 mt-3">
      <input
        type="text"
        value={guidance}
        onChange={(e) => setGuidance(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleRevise()}
        placeholder="AI revision (e.g. 'update description to mention new exhibits')"
        className="flex-1 text-xs px-2.5 py-1.5 rounded-lg border border-input bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        disabled={revising}
      />
      <button
        onClick={handleRevise}
        disabled={revising || !guidance.trim()}
        className="text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1"
      >
        {revising ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
        Revise
      </button>
    </div>
  );
}

interface DBEvent {
  id: string;
  title: string;
  description: string;
  category: string;
  venue_name: string;
  venue_address: string;
  lat: number | null;
  lng: number | null;
  source_url: string;
  source_name: string;
  discovery_source: string;
  official_website: string;
  map_url: string;
  schedule_notes: string;
  start_date: string | null;
  end_date: string | null;
  cost_level: string;
  cost_details: string;
  indoor_outdoor: string;
  age_range: string;
  crowd_level: string;
  intensity: number;
  novelty_score: number;
  weather_sensitivity: number;
  estimated_duration: string;
  drive_minutes_from_lwr: number;
  parent_id: string | null;
  image_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  save_count: number;
  link_click_count: number;
  is_curated: boolean;
  curated_at: string | null;
  curated_notes: string;
  curated_source: string;
  region_tag: string;
}

interface EmailLogItem {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

interface SuppressedEmail {
  id: string;
  email: string;
  reason: string;
  created_at: string;
}

interface WizardResponse {
  id: string;
  created_at: string;
  group_size: string | null;
  timeframe_label: string | null;
  budget_level: string | null;
  indoor_outdoor: string | null;
  has_local_passes: boolean | null;
  timeframe_start: string | null;
  timeframe_end: string | null;
  budget_max_dollars: number | null;
  weather_preference: string | null;
  weather_exposure: number | null;
  duration_preference: string | null;
  origin_zip: string | null;
  max_drive_minutes: number | null;
  extras_length: number | null;
  extras_signals: {
    boostKeywords?: string[];
    penalizeKeywords?: string[];
    hasMembership?: string[];
    recentActivities?: string[];
    boostIndoor?: boolean;
    boostOutdoor?: boolean;
  } | null;
}

interface ScrapeCandidate {
  id: string;
  title: string;
  data: any;
  source_urls: string[];
  match_existing_id: string | null;
  action: string;
  status: string;
  rejection_reason: string | null;
  batch_id: string | null;
  created_at: string;
}

type SortKey = keyof DBEvent;
type AdminTab = "events" | "review" | "feedback" | "emails" | "stats" | "reference";

interface FeedbackRow {
  id: string;
  email: string | null;
  message: string;
  page_url: string | null;
  created_at: string;
  rating: string | null;
  plan_share_token: string | null;
  is_read?: boolean | null;
}

function AdminEventsPage() {
  const [events, setEvents] = useState<DBEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<DBEvent>>({});
  const [tab, setTab] = useState<AdminTab>("events");
  const [feedback, setFeedback] = useState<FeedbackRow[]>([]);
  const [emailLog, setEmailLog] = useState<EmailLogItem[]>([]);
  const [suppressed, setSuppressed] = useState<SuppressedEmail[]>([]);
  const [suppressInput, setSuppressInput] = useState("");
  const [wizardResponses, setWizardResponses] = useState<WizardResponse[]>([]);
  const [userStats, setUserStats] = useState<Array<{ user_id: string; email: string; session_count: number; first_login_at: string; last_login_at: string }>>([]);
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");
  const [filterType, setFilterType] = useState<"all" | EventType>("all");
  const [filterMetro, setFilterMetro] = useState<"all" | EventMetro>("all");
  const [filterCurated, setFilterCurated] = useState<"all" | "curated" | "uncurated">("all");

  // Refresh form state
  const [scraping, setScraping] = useState(false);
  const [refreshForm, setRefreshForm] = useState({
    searchType: "both" as "destinations" | "events" | "both",
    radiusMinutes: 30,
    maxResults: 10,
    dateFrom: "",
    dateTo: "",
    sourceUrls: "",
  });

  // Google Places discover form state
  const [gpDiscovering, setGpDiscovering] = useState(false);
  const [gpForm, setGpForm] = useState({
    searchType: "destinations" as "destinations" | "events",
    query: "",
    zip: "34202",
    radiusMinutes: 30,
    maxResults: 10,
    minRating: 4.0,
    minReviewCount: 50,
  });

  // Review queue state
  const [candidates, setCandidates] = useState<ScrapeCandidate[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
  const [showProcessed, setShowProcessed] = useState(false);

  const refreshCandidatesFn = useServerFn(refreshCandidates);
  const discoverFromGooglePlacesFn = useServerFn(discoverFromGooglePlaces);
  const approveCandidatesFn = useServerFn(approveCandidates);
  const rejectCandidatesFn = useServerFn(rejectCandidates);
  const runFullSeedFn = useServerFn(runFullSeed);
  const getSeedContextFn = useServerFn(getSeedContext);
  const auditTierCoverageFn = useServerFn(auditTierCoverage);
  const fetchEventsFn = useServerFn(adminFetchEvents);
  const fetchCandidatesServerFn = useServerFn(adminFetchCandidates);
  const fetchEmailLogFn = useServerFn(adminFetchEmailLog);
  const fetchSuppressedFn = useServerFn(adminFetchSuppressed);
  const addSuppressionFn = useServerFn(adminAddSuppression);
  const fetchWizardResponsesFn = useServerFn(adminFetchWizardResponses);
  const fetchUserStatsFn = useServerFn(adminFetchUserStats);
  const fetchAllAdminFn = useServerFn(adminFetchAllFn);
  const updateEventFn = useServerFn(adminUpdateEvent);
  const deleteEventsFn = useServerFn(adminDeleteEvents);
  const bulkSetCuratedFn = useServerFn(adminBulkSetCurated);
  const deleteFeedbackFn = useServerFn(adminDeleteFeedback);
  const markFeedbackReadFn = useServerFn(adminMarkFeedbackRead);
  const getAdminToken = () =>
    typeof window !== "undefined" ? localStorage.getItem(ADMIN_TOKEN_KEY) || "" : "";
  const [seeding, setSeeding] = useState(false);
  const [seedElapsed, setSeedElapsed] = useState(0);
  const [seedModelLabel, setSeedModelLabel] = useState<string>("");
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [seedContext, setSeedContext] = useState<{ totalDestinations: number } | null>(null);
  const [lastSeedTier, setLastSeedTier] = useState<SeedTier>("flagship");
  const [useProModel, setUseProModel] = useState<boolean>(false);
  const [seedOriginZip, setSeedOriginZip] = useState<string>("34202");
  const [seedRegionLabel, setSeedRegionLabel] = useState<string>("");
  const [seedRadiusMinutes, setSeedRadiusMinutes] = useState<number>(60);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState<Awaited<ReturnType<typeof auditTierCoverage>> | null>(null);
  const [auditTier, setAuditTier] = useState<SeedTier>("flagship");

  // Bulk selection state for events table
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkGuidance, setBulkGuidance] = useState("");
  const [bulkRevising, setBulkRevising] = useState(false);
  const [bulkRevisionProgress, setBulkRevisionProgress] = useState<{ done: number; total: number } | null>(null);
  const reviseEventFn = useServerFn(reviseEventWithAI);
  const recalibrateScoresFn = useServerFn(recalibrateEventScores);
  const fetchScoreProposalsFn = useServerFn(fetchScoreProposals);
  const decideScoreProposalsFn = useServerFn(decideScoreProposals);
  const backfillScheduleNotesFn = useServerFn(backfillScheduleNotes);
  const fetchNotesProposalsFn = useServerFn(fetchScheduleNotesProposals);
  const decideNotesProposalsFn = useServerFn(decideScheduleNotesProposals);
  const [scoreProposals, setScoreProposals] = useState<any[]>([]);
  const [notesProposals, setNotesProposals] = useState<any[]>([]);
  const [aiOpsRunning, setAiOpsRunning] = useState<"score" | "notes" | null>(null);

  async function loadProposals() {
    try {
      const [s, n] = await Promise.all([
        fetchScoreProposalsFn({ data: { token: getAdminToken() } }),
        fetchNotesProposalsFn({ data: { token: getAdminToken() } }),
      ]);
      setScoreProposals(s as any[]);
      setNotesProposals(n as any[]);
    } catch { /* noop */ }
  }
  async function runScoreRecalibrate() {
    setAiOpsRunning("score");
    try {
      const r = await recalibrateScoresFn({ data: { token: getAdminToken(), limit: 25 } });
      toast.success(`Generated ${r.count} score proposals (scanned ${r.scanned})`);
      await loadProposals();
    } catch (e) { toast.error(e instanceof Error ? e.message : String(e)); }
    setAiOpsRunning(null);
  }
  async function runNotesBackfill() {
    setAiOpsRunning("notes");
    try {
      const r = await backfillScheduleNotesFn({ data: { token: getAdminToken(), limit: 10 } });
      toast.success(`Generated ${r.count} schedule note proposals (scanned ${r.scanned})`);
      await loadProposals();
    } catch (e) { toast.error(e instanceof Error ? e.message : String(e)); }
    setAiOpsRunning(null);
  }
  async function decideScores(ids: string[], decision: "approve" | "reject") {
    try {
      await decideScoreProposalsFn({ data: { token: getAdminToken(), ids, decision } });
      await loadProposals();
      if (decision === "approve") fetchAll();
    } catch (e) { toast.error(e instanceof Error ? e.message : String(e)); }
  }
  async function decideNotes(ids: string[], decision: "approve" | "reject") {
    try {
      await decideNotesProposalsFn({ data: { token: getAdminToken(), ids, decision } });
      await loadProposals();
      if (decision === "approve") fetchAll();
    } catch (e) { toast.error(e instanceof Error ? e.message : String(e)); }
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("rp_last_seed_tier") as SeedTier | null;
      if (stored && (["flagship", "notable", "niche", "deep-cut"] as const).includes(stored as SeedTier)) {
        setLastSeedTier(stored);
        setAuditTier(stored);
      }
      const storedPro = localStorage.getItem("rp_seed_use_pro_model");
      if (storedPro === "1") setUseProModel(true);
      const storedZip = localStorage.getItem("rp_seed_origin_zip");
      if (storedZip && /^\d{5}$/.test(storedZip)) setSeedOriginZip(storedZip);
      const storedRegion = localStorage.getItem("rp_seed_region_label");
      if (storedRegion) setSeedRegionLabel(storedRegion);
      const storedRadius = localStorage.getItem("rp_seed_radius_minutes");
      if (storedRadius && Number.isFinite(parseInt(storedRadius))) setSeedRadiusMinutes(parseInt(storedRadius));
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchFeedback();
    fetchEmailData();
    fetchWizardResponses();
    fetchUserStats();
    fetchCandidates();
  }, []);

  // Live elapsed-time ticker while a seed is running, so the operator can see
  // progress and judge whether the worker has gone past the ~100s upstream cap.
  useEffect(() => {
    if (!seeding) {
      setSeedElapsed(0);
      return;
    }
    const startedAt = Date.now();
    setSeedElapsed(0);
    const id = setInterval(() => {
      setSeedElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [seeding]);

  async function fetchAll() {
    setLoading(true);
    try {
      const rows = await fetchEventsFn({ data: { token: getAdminToken() } });
      setEvents(rows as unknown as DBEvent[]);
    } catch (err) {
      toast.error("Failed to load events: " + (err instanceof Error ? err.message : String(err)));
    }
    setLoading(false);
  }

  async function fetchFeedback() {
    try {
      const all = await fetchAllAdminFn({ data: { token: getAdminToken() } });
      setFeedback(all.feedback as unknown as FeedbackRow[]);
    } catch { /* noop */ }
  }

  async function fetchEmailData() {
    try {
      const [logs, supp] = await Promise.all([
        fetchEmailLogFn({ data: { token: getAdminToken() } }),
        fetchSuppressedFn({ data: { token: getAdminToken() } }),
      ]);
      setEmailLog(logs as EmailLogItem[]);
      setSuppressed(supp as SuppressedEmail[]);
    } catch { /* noop */ }
  }

  async function fetchWizardResponses() {
    try {
      const rows = await fetchWizardResponsesFn({ data: { token: getAdminToken() } });
      setWizardResponses(rows as WizardResponse[]);
    } catch { /* noop */ }
  }

  async function fetchUserStats() {
    try {
      const rows = await fetchUserStatsFn({ data: { token: getAdminToken() } });
      setUserStats(rows as typeof userStats);
    } catch { /* noop */ }
  }

  async function fetchCandidates() {
    try {
      const rows = await fetchCandidatesServerFn({ data: { token: getAdminToken() } });
      setCandidates(rows as ScrapeCandidate[]);
    } catch { /* noop */ }
  }

  const toggleActive = async (event: DBEvent) => {
    try {
      await updateEventFn({ data: { token: getAdminToken(), id: event.id, patch: { is_active: !event.is_active } } });
      setEvents((prev) => prev.map((e) => e.id === event.id ? { ...e, is_active: !e.is_active } : e));
      toast.success(`${event.title} ${!event.is_active ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error("Failed to update status: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const toggleCurated = async (event: DBEvent) => {
    const next = !event.is_curated;
    try {
      await bulkSetCuratedFn({ data: { token: getAdminToken(), ids: [event.id], curated: next } });
      const stamp = next ? new Date().toISOString() : null;
      setEvents((prev) => prev.map((e) => e.id === event.id ? { ...e, is_curated: next, curated_at: stamp } : e));
      toast.success(`${event.title} ${next ? "marked curated" : "un-curated"}`);
    } catch (err) {
      toast.error("Failed to toggle curated: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const handleBulkSetCurated = async (curated: boolean) => {
    if (selectedEventIds.size === 0) return;
    const ids = Array.from(selectedEventIds);
    try {
      await bulkSetCuratedFn({ data: { token: getAdminToken(), ids, curated } });
      const stamp = curated ? new Date().toISOString() : null;
      setEvents((prev) => prev.map((e) => selectedEventIds.has(e.id) ? { ...e, is_curated: curated, curated_at: stamp } : e));
      toast.success(`${ids.length} ${curated ? "marked curated" : "un-curated"}`);
    } catch (err) {
      toast.error("Bulk curate failed: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const startEdit = (event: DBEvent) => {
    setEditingId(event.id);
    setExpandedId(event.id);
    setEditForm({
      title: event.title,
      description: event.description,
      category: event.category,
      venue_name: event.venue_name,
      schedule_notes: event.schedule_notes,
      start_date: event.start_date,
      end_date: event.end_date,
      cost_level: event.cost_level,
      cost_details: event.cost_details,
      indoor_outdoor: event.indoor_outdoor,
      age_range: event.age_range,
      crowd_level: event.crowd_level,
      novelty_score: event.novelty_score,
      intensity: event.intensity,
      weather_sensitivity: event.weather_sensitivity,
      estimated_duration: event.estimated_duration,
      drive_minutes_from_lwr: event.drive_minutes_from_lwr,
      source_url: event.source_url,
      official_website: event.official_website,
      map_url: event.map_url,
      curated_notes: event.curated_notes,
      region_tag: event.region_tag,
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await updateEventFn({ data: { token: getAdminToken(), id: editingId, patch: editForm as Record<string, unknown> } });
      setEvents((prev) => prev.map((e) => e.id === editingId ? { ...e, ...editForm } : e));
      toast.success("Event updated!");
      setEditingId(null);
    } catch (err) {
      toast.error("Failed to save changes: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  // ===== Bulk actions =====
  const toggleEventSelection = (id: string) => {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearEventSelection = () => setSelectedEventIds(new Set());

  const handleBulkDelete = async () => {
    if (selectedEventIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedEventIds);
    try {
      await deleteEventsFn({ data: { token: getAdminToken(), ids } });
      setEvents((prev) => prev.filter((e) => !selectedEventIds.has(e.id)));
      toast.success(`Deleted ${ids.length} event${ids.length === 1 ? "" : "s"}`);
      clearEventSelection();
      setBulkDeleteOpen(false);
    } catch (err) {
      toast.error(`Bulk delete failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    setBulkDeleting(false);
  };

  const handleBulkAIRevise = async () => {
    const guidance = bulkGuidance.trim();
    if (!guidance || selectedEventIds.size === 0) return;
    const ids = Array.from(selectedEventIds);
    setBulkRevising(true);
    setBulkRevisionProgress({ done: 0, total: ids.length });
    let succeeded = 0;
    let failed = 0;
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      try {
        const result = await reviseEventFn({ data: { eventId: id, guidance, token: getAdminToken() } });
        if (result.success && result.changedFields) {
          succeeded++;
          setEvents((prev) => prev.map((e) => e.id === id ? { ...e, ...result.changedFields } : e));
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
      setBulkRevisionProgress({ done: i + 1, total: ids.length });
    }
    setBulkRevising(false);
    setBulkRevisionProgress(null);
    setBulkGuidance("");
    if (succeeded > 0) {
      toast.success(`Revised ${succeeded} event${succeeded === 1 ? "" : "s"}${failed > 0 ? ` (${failed} failed)` : ""}`);
    } else {
      toast.error(`AI revision failed for all ${failed} events`);
    }
    if (failed === 0) clearEventSelection();
  };

  const deleteFeedback = async (id: string) => {
    if (!confirm("Delete this feedback permanently?")) return;
    try {
      await deleteFeedbackFn({ data: { token: getAdminToken(), id } });
      setFeedback((prev) => prev.filter((f) => f.id !== id));
      toast.success("Feedback deleted");
    } catch (err) {
      toast.error("Failed to delete: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const toggleFeedbackRead = async (fb: FeedbackRow) => {
    const next = !fb.is_read;
    setFeedback((prev) => prev.map((f) => f.id === fb.id ? { ...f, is_read: next } : f));
    try {
      await markFeedbackReadFn({ data: { token: getAdminToken(), id: fb.id, isRead: next } });
    } catch (err) {
      setFeedback((prev) => prev.map((f) => f.id === fb.id ? { ...f, is_read: !next } : f));
      toast.error("Failed to update: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  // --- NEW: Parameterized refresh ---
  const handleRefresh = async () => {
    setScraping(true);
    toast.info("Searching for new experiences...");

    try {
      const sourceUrls = refreshForm.sourceUrls.trim()
        ? refreshForm.sourceUrls.split("\n").map(u => u.trim()).filter(Boolean)
        : undefined;

      const result = await refreshCandidatesFn({
        data: {
          searchType: refreshForm.searchType,
          radiusMinutes: refreshForm.radiusMinutes,
          maxResults: refreshForm.maxResults,
          dateFrom: refreshForm.dateFrom || undefined,
          dateTo: refreshForm.dateTo || undefined,
          sourceUrls,
          token: getAdminToken(),
        },
      });

      if (result.success) {
        toast.success(`Found ${result.count} new candidates! Review them in the Pending Review tab.`);
        fetchCandidates();
        if (result.count > 0) setTab("review");
      } else {
        toast.error(result.error || "Refresh failed");
      }
    } catch (err) {
      toast.error("Refresh error: " + (err instanceof Error ? err.message : String(err)));
    }
    setScraping(false);
  };

  const handleGooglePlacesDiscover = async () => {
    if (!gpForm.query.trim()) {
      toast.error("Enter a search query first.");
      return;
    }
    setGpDiscovering(true);
    toast.info("Searching Google Places…");
    try {
      const result = await discoverFromGooglePlacesFn({
        data: {
          searchType: gpForm.searchType,
          query: gpForm.query.trim(),
          zip: gpForm.zip,
          radiusMinutes: gpForm.radiusMinutes,
          maxResults: gpForm.maxResults,
          minRating: gpForm.minRating,
          minReviewCount: gpForm.minReviewCount,
          token: getAdminToken(),
        },
      });
      if (result.count > 0) {
        toast.success(result.message || `Added ${result.count} candidates.`);
        fetchCandidates();
      } else {
        toast.message(result.message || "No new candidates added.");
      }
    } catch (err) {
      toast.error("Google Places error: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setGpDiscovering(false);
    }
  };

  const openSeedDialog = async () => {
    try {
      const ctx = await getSeedContextFn({ data: { token: getAdminToken() } });
      setSeedContext(ctx);
      setTierDialogOpen(true);
    } catch (err) {
      toast.error("Could not load seed context: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const handleAISeed = async (tier: SeedTier) => {
    setTierDialogOpen(false);
    setSeeding(true);
    if (typeof window !== "undefined") {
      localStorage.setItem("rp_last_seed_tier", tier);
      localStorage.setItem("rp_seed_use_pro_model", useProModel ? "1" : "0");
      localStorage.setItem("rp_seed_origin_zip", seedOriginZip);
      localStorage.setItem("rp_seed_region_label", seedRegionLabel);
      localStorage.setItem("rp_seed_radius_minutes", String(seedRadiusMinutes));
    }
    setLastSeedTier(tier);
    const modelLabel = useProModel ? "Pro (slower)" : "Flash";
    setSeedModelLabel(modelLabel);
    toast.info(`Seeding up to 10 ${tier} destinations near ${seedOriginZip} with ${modelLabel}…`);
    try {
      const result = await runFullSeedFn({ data: { token: getAdminToken(), tier, useProModel, originZip: seedOriginZip, regionLabel: seedRegionLabel, radiusMinutes: seedRadiusMinutes } });
      if (result.success) {
        toast.success(
          `Seeded ${result.inserted} destination${result.inserted === 1 ? "" : "s"} via ${result.modelUsed.replace("google/", "")} (filtered ${result.lowConfidenceFiltered} low-conf, deduped ${result.deduped}). Run again for more.`
        );
        if (result.saturationHint) {
          toast.info(result.saturationHint, { duration: 10000 });
        }
        if (result.tier === "deep-cut" && result.generated < 5) {
          toast.info(
            `Model returned only ${result.generated} candidates — you may be approaching saturation.`,
            { duration: 10000 }
          );
        }
        fetchCandidates();
        setTab("review");
      } else {
        const msg = result.error || "Seed failed";
        const isTimeout = /timed out|TimeoutError|AbortError|upstream request timeout/i.test(msg);
        if (isTimeout) {
          fetchCandidates();
          toast.error(
            "Request timed out. Check the review queue — partial results may still have been inserted.",
            { duration: 10000 },
          );
        } else {
          toast.error(`Seed failed: ${msg}`);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isTimeout = /timed out|TimeoutError|AbortError|upstream request timeout/i.test(msg);
      if (isTimeout) {
        fetchCandidates();
        toast.error(
          "Request timed out. Check the review queue — partial results may still have been inserted.",
          { duration: 10000 },
        );
      } else {
        toast.error("Seed error: " + msg);
      }
    }
    setSeeding(false);
  };

  const runAudit = async (tier: SeedTier) => {
    setAuditing(true);
    setAuditTier(tier);
    setAuditResult(null);
    try {
      const result = await auditTierCoverageFn({ data: { token: getAdminToken(), tier, originZip: seedOriginZip, regionLabel: seedRegionLabel, radiusMinutes: seedRadiusMinutes } });
      setAuditResult(result);
    } catch (err) {
      toast.error("Audit error: " + (err instanceof Error ? err.message : String(err)));
    }
    setAuditing(false);
  };
  const handleApproveSelected = async () => {
    if (selectedCandidates.size === 0) return;
    const ids = Array.from(selectedCandidates);
    try {
      const result = await approveCandidatesFn({ data: { candidateIds: ids, token: getAdminToken() } });
      if (result.success) {
        toast.success(`Approved ${result.approved} events!`);
        if (result.errors?.length) toast.error(`${result.errors.length} failed: ${result.errors[0]}`);
        setSelectedCandidates(new Set());
        fetchCandidates();
        fetchAll();
      } else {
        toast.error(result.error || "Approve failed");
      }
    } catch (err) {
      toast.error("Approve error: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const handleRejectSelected = async () => {
    if (selectedCandidates.size === 0) return;
    const reason = prompt("Rejection reason (optional):");
    const ids = Array.from(selectedCandidates);
    try {
      const result = await rejectCandidatesFn({ data: { candidateIds: ids, reason: reason || undefined, token: getAdminToken() } });
      if (result.success) {
        toast.success("Candidates rejected");
        setSelectedCandidates(new Set());
        fetchCandidates();
      } else {
        toast.error(result.error || "Reject failed");
      }
    } catch (err) {
      toast.error("Reject error: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  // --- One-click approve/reject ---
  const handleQuickApprove = async (candidateId: string) => {
    try {
      const result = await approveCandidatesFn({ data: { candidateIds: [candidateId], token: getAdminToken() } });
      if (result.success) {
        toast.success(`Approved!`);
        setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: "approved" } : c));
        fetchAll();
      } else {
        toast.error(result.error || "Approve failed");
      }
    } catch (err) {
      toast.error("Approve error: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const handleQuickReject = async (candidateId: string) => {
    const reason = prompt("Rejection reason (optional):");
    try {
      const result = await rejectCandidatesFn({ data: { candidateIds: [candidateId], reason: reason || undefined, token: getAdminToken() } });
      if (result.success) {
        toast.success("Rejected");
        setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: "rejected", rejection_reason: reason || "Rejected by admin" } : c));
      } else {
        toast.error(result.error || "Reject failed");
      }
    } catch (err) {
      toast.error("Reject error: " + (err instanceof Error ? err.message : String(err)));
    }
  };

  const toggleCandidateSelection = (id: string) => {
    setSelectedCandidates(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const filtered = useMemo(() => {
    let list = [...events];
    if (filterActive === "active") list = list.filter((e) => e.is_active);
    if (filterActive === "inactive") list = list.filter((e) => !e.is_active);
    if (filterType !== "all") list = list.filter((e) => getEventType(e) === filterType);
    if (filterMetro !== "all") list = list.filter((e) => getEventMetro(e) === filterMetro);
    if (filterCurated === "curated") list = list.filter((e) => e.is_curated);
    if (filterCurated === "uncurated") list = list.filter((e) => !e.is_curated);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) =>
        e.title.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q) ||
        e.venue_name.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = typeof aVal === "string" ? aVal.localeCompare(bVal as string) : (aVal as number) - (bVal as number);
      return sortAsc ? cmp : -cmp;
    });
    return list;
  }, [events, search, sortKey, sortAsc, filterActive, filterType, filterMetro, filterCurated]);

  const topSavedEvents = useMemo(() =>
    [...events].filter(e => e.save_count > 0).sort((a, b) => b.save_count - a.save_count).slice(0, 15),
    [events]
  );
  const topClickedEvents = useMemo(() =>
    [...events].filter(e => e.link_click_count > 0).sort((a, b) => b.link_click_count - a.link_click_count).slice(0, 15),
    [events]
  );

  const wizardStats = useMemo(() => {
    if (!wizardResponses.length) return null;
    const total = wizardResponses.length;

    // --- Budget vibe (derive from budget_max_dollars; fall back to legacy budget_level) ---
    const budgetVibes: Record<string, number> = { "Free": 0, "Under $25": 0, "Under $75": 0, "No limit": 0 };
    const vibeFromExcludes = (s: string | null): keyof typeof budgetVibes | null => {
      if (!s) return null;
      const exc = s.split(",").map((x) => x.trim());
      if (exc.includes("low")) return "Free";
      if (exc.includes("medium")) return "Under $25";
      if (exc.includes("high")) return "Under $75";
      return "No limit";
    };
    for (const r of wizardResponses) {
      let label: keyof typeof budgetVibes | null = null;
      if (r.budget_max_dollars === 0) label = "Free";
      else if (r.budget_max_dollars === 25) label = "Under $25";
      else if (r.budget_max_dollars === 75) label = "Under $75";
      else if (r.budget_max_dollars == null) label = vibeFromExcludes(r.budget_level);
      else label = "No limit";
      if (label) budgetVibes[label]++;
    }

    // --- Timeframe analysis from real dates ---
    const leadTime: Record<string, number> = { "Same-day": 0, "Next-day": 0, "2–7 days": 0, "8–30 days": 0, "30+ days": 0 };
    const tripLength: Record<string, number> = { "1 day": 0, "2 days": 0, "3–5 days": 0, "6+ days": 0 };
    const topDates: Record<string, number> = {};
    for (const r of wizardResponses) {
      if (r.timeframe_start) {
        topDates[r.timeframe_start] = (topDates[r.timeframe_start] || 0) + 1;
        const created = new Date(r.created_at);
        const start = new Date(r.timeframe_start + "T12:00:00");
        const days = Math.round((start.getTime() - new Date(created.toISOString().split("T")[0] + "T12:00:00").getTime()) / 86400000);
        if (days <= 0) leadTime["Same-day"]++;
        else if (days === 1) leadTime["Next-day"]++;
        else if (days <= 7) leadTime["2–7 days"]++;
        else if (days <= 30) leadTime["8–30 days"]++;
        else leadTime["30+ days"]++;
      }
      if (r.timeframe_start && r.timeframe_end) {
        const start = new Date(r.timeframe_start + "T12:00:00");
        const end = new Date(r.timeframe_end + "T12:00:00");
        const len = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
        if (len <= 1) tripLength["1 day"]++;
        else if (len === 2) tripLength["2 days"]++;
        else if (len <= 5) tripLength["3–5 days"]++;
        else tripLength["6+ days"]++;
      }
    }
    const topDatesSorted = Object.entries(topDates).sort((a, b) => b[1] - a[1]).slice(0, 10);

    // --- Weather preference ---
    const weatherPref: Record<string, number> = { indoor: 0, either: 0, outdoor: 0 };
    for (const r of wizardResponses) {
      let pref = r.weather_preference;
      if (!pref && r.weather_exposure != null) {
        pref = r.weather_exposure <= -1 ? "indoor" : r.weather_exposure >= 1 ? "outdoor" : "either";
      }
      if (pref && pref in weatherPref) weatherPref[pref]++;
    }

    // --- Duration preference ---
    const durations: Record<string, number> = {};
    for (const r of wizardResponses) {
      if (r.duration_preference) durations[r.duration_preference] = (durations[r.duration_preference] || 0) + 1;
    }

    // --- Origin / drive time ---
    const zips: Record<string, number> = {};
    let driveSum = 0, driveN = 0;
    for (const r of wizardResponses) {
      if (r.origin_zip) zips[r.origin_zip] = (zips[r.origin_zip] || 0) + 1;
      if (r.max_drive_minutes != null) { driveSum += r.max_drive_minutes; driveN++; }
    }
    const topZips = Object.entries(zips).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const avgDrive = driveN ? Math.round(driveSum / driveN) : null;

    // --- Extras keywords ---
    let extrasFilled = 0;
    let extrasLenSum = 0;
    let passCount = 0;
    const boost: Record<string, number> = {};
    const penalize: Record<string, number> = {};
    for (const r of wizardResponses) {
      if ((r.extras_length ?? 0) > 0) { extrasFilled++; extrasLenSum += (r.extras_length || 0); }
      const sig = r.extras_signals;
      if (sig?.hasMembership && sig.hasMembership.length > 0) passCount++;
      else if (r.has_local_passes) passCount++;
      if (sig?.boostKeywords) for (const k of sig.boostKeywords) boost[k] = (boost[k] || 0) + 1;
      if (sig?.penalizeKeywords) for (const k of sig.penalizeKeywords) penalize[k] = (penalize[k] || 0) + 1;
    }
    const topBoost = Object.entries(boost).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const topPenalize = Object.entries(penalize).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const avgExtrasLen = extrasFilled ? Math.round(extrasLenSum / extrasFilled) : 0;

    return {
      total,
      budgetVibes,
      leadTime, tripLength, topDates: topDatesSorted,
      weatherPref,
      durations,
      topZips, avgDrive,
      extrasFilled, avgExtrasLen, topBoost, topPenalize, passCount,
    };
  }, [wizardResponses]);

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <th
      className="px-2 py-1.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none whitespace-nowrap"
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === field && (sortAsc ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </span>
    </th>
  );

  const pendingCandidates = candidates.filter(c => c.status === "pending");
  const processedCandidates = candidates.filter(c => c.status !== "pending");

  const statusColor = (status: string) => {
    switch (status) {
      case "sent": return "bg-highlight/10 text-highlight";
      case "failed": return "bg-destructive/10 text-destructive";
      case "pending": return "bg-primary/10 text-primary";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const StatBar = ({ label, count, total, color }: { label: string; count: number; total: number; color: string }) => (
    <div className="flex items-center gap-3 text-xs">
      <span className="w-28 text-muted-foreground truncate">{label}</span>
      <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.round((count / total) * 100)}%`, background: color }} />
      </div>
      <span className="font-bold text-foreground w-8 text-right">{count}</span>
    </div>
  );

  return (
    <div className="min-h-screen py-16 px-4">
      <div className="max-w-[95vw] mx-auto">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setTab("events")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === "events" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            Events ({events.length})
          </button>
          <button onClick={() => { setTab("review"); fetchCandidates(); loadProposals(); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all relative ${tab === "review" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            Pending Review
            {pendingCandidates.length > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">{pendingCandidates.length}</span>
            )}
          </button>
          <button onClick={() => setTab("feedback")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === "feedback" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            Feedback ({feedback.filter((f) => !f.is_read).length})
          </button>
          <button onClick={() => { setTab("emails"); fetchEmailData(); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all inline-flex items-center gap-1.5 ${tab === "emails" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            <Mail className="h-3.5 w-3.5" /> Emails
          </button>
          <button onClick={() => { setTab("stats"); fetchWizardResponses(); fetchUserStats(); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all inline-flex items-center gap-1.5 ${tab === "stats" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            <BarChart3 className="h-3.5 w-3.5" /> Stats
          </button>
          <button onClick={() => setTab("reference")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all inline-flex items-center gap-1.5 ${tab === "reference" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            <BookOpen className="h-3.5 w-3.5" /> System Reference
          </button>
          <div className="ml-auto">
            <AdminSignOutButton />
          </div>
        </div>

        {/* ===== EVENTS TAB ===== */}
        {tab === "events" && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-black tracking-tight">Events Database</h1>
                <p className="text-muted-foreground text-sm mt-1">
                  {loading ? "Loading..." : `${filtered.length} of ${events.length} events`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select value={filterActive} onChange={(e) => setFilterActive(e.target.value as any)}
                  className="rounded-xl border border-input bg-card px-3 py-2.5 text-sm font-medium">
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Disabled Only</option>
                </select>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value as any)}
                  className="rounded-xl border border-input bg-card px-3 py-2.5 text-sm font-medium"
                  title="Filter by event type">
                  <option value="all">All Types</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="limited">Limited Time</option>
                </select>
                <select value={filterMetro} onChange={(e) => setFilterMetro(e.target.value as any)}
                  className="rounded-xl border border-input bg-card px-3 py-2.5 text-sm font-medium"
                  title="Filter by metro area">
                  <option value="all">All Metros</option>
                  <option value="sarasota">{METRO_LABELS.sarasota}</option>
                  <option value="stpete">{METRO_LABELS.stpete}</option>
                  <option value="tampa">{METRO_LABELS.tampa}</option>
                </select>
                <select value={filterCurated} onChange={(e) => setFilterCurated(e.target.value as any)}
                  className="rounded-xl border border-input bg-card px-3 py-2.5 text-sm font-medium"
                  title="Filter by human-curated status">
                  <option value="all">All Curation</option>
                  <option value="curated">⭐ Curated only</option>
                  <option value="uncurated">Uncurated only</option>
                </select>
                <div className="relative w-full sm:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search events..."
                    className="w-full rounded-xl border border-input bg-card pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="bg-card rounded-2xl border border-border p-12 text-center">
                <p className="text-muted-foreground animate-pulse">Loading events database...</p>
              </div>
            ) : (
              <>
                {selectedEventIds.size > 0 && (
                  <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 rounded-xl border border-primary/30 bg-primary/5">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-foreground">
                        {selectedEventIds.size} selected
                      </span>
                      <button
                        onClick={clearEventSelection}
                        className="text-xs text-muted-foreground hover:text-foreground underline"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:items-center">
                      <input
                        type="text"
                        value={bulkGuidance}
                        onChange={(e) => setBulkGuidance(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleBulkAIRevise(); }}
                        placeholder="AI revision for all selected (e.g. 'shorten descriptions to one sentence')"
                        disabled={bulkRevising || bulkDeleting}
                        className="flex-1 text-xs px-3 py-2 rounded-lg border border-input bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                      <button
                        onClick={handleBulkAIRevise}
                        disabled={bulkRevising || bulkDeleting || !bulkGuidance.trim()}
                        className="text-xs px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1.5 font-semibold whitespace-nowrap"
                      >
                        {bulkRevising
                          ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />{bulkRevisionProgress ? ` ${bulkRevisionProgress.done}/${bulkRevisionProgress.total}` : ""}</>
                          : <><Sparkles className="h-3.5 w-3.5" /> Revise All</>}
                      </button>
                      <button
                        onClick={() => handleBulkSetCurated(true)}
                        disabled={bulkRevising || bulkDeleting}
                        className="text-xs px-3 py-2 rounded-lg bg-highlight/20 text-highlight hover:bg-highlight/30 disabled:opacity-50 inline-flex items-center gap-1.5 font-semibold whitespace-nowrap"
                        title="Mark selected as human-curated (feeds the AI learning context)"
                      >
                        <Star className="h-3.5 w-3.5 fill-current" /> Mark Curated
                      </button>
                      <button
                        onClick={() => handleBulkSetCurated(false)}
                        disabled={bulkRevising || bulkDeleting}
                        className="text-xs px-3 py-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1.5 font-semibold whitespace-nowrap"
                      >
                        <Star className="h-3.5 w-3.5" /> Un-curate
                      </button>
                      <button
                        onClick={() => setBulkDeleteOpen(true)}
                        disabled={bulkRevising || bulkDeleting}
                        className="text-xs px-3 py-2 rounded-lg bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50 inline-flex items-center gap-1.5 font-semibold whitespace-nowrap"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                )}
                <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-b border-border">
                      <tr>
                        <th className="px-2 py-1.5 text-left w-8">
                          <input
                            type="checkbox"
                            aria-label="Select all visible events"
                            checked={filtered.length > 0 && filtered.every((e) => selectedEventIds.has(e.id))}
                            ref={(el) => {
                              if (el) {
                                const someSelected = filtered.some((e) => selectedEventIds.has(e.id));
                                const allSelected = filtered.length > 0 && filtered.every((e) => selectedEventIds.has(e.id));
                                el.indeterminate = someSelected && !allSelected;
                              }
                            }}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedEventIds((prev) => {
                                  const next = new Set(prev);
                                  filtered.forEach((ev) => next.add(ev.id));
                                  return next;
                                });
                              } else {
                                setSelectedEventIds((prev) => {
                                  const next = new Set(prev);
                                  filtered.forEach((ev) => next.delete(ev.id));
                                  return next;
                                });
                              }
                            }}
                            className="h-3.5 w-3.5 rounded border-input cursor-pointer"
                          />
                        </th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-muted-foreground uppercase w-8"></th>
                        <SortHeader label="Title" field="title" />
                        <SortHeader label="Category" field="category" />
                        <SortHeader label="Venue" field="venue_name" />
                        <SortHeader label="Schedule" field="start_date" />
                        <SortHeader label="Cost" field="cost_level" />
                        <SortHeader label="Date Added" field="created_at" />
                        <SortHeader label="Novelty" field="novelty_score" />
                        <SortHeader label="Intensity" field="intensity" />
                        <th className="px-2 py-1.5 text-center text-[10px] font-semibold text-muted-foreground uppercase whitespace-nowrap">Stats</th>
                        <th className="px-2 py-1.5 text-center text-[10px] font-semibold text-muted-foreground uppercase w-8" title="Human-curated">⭐</th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-muted-foreground uppercase w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filtered.map((event) => (
                        <EventRow
                          key={event.id}
                          event={event}
                          expanded={expandedId === event.id}
                          editing={editingId === event.id}
                          editForm={editForm}
                          selected={selectedEventIds.has(event.id)}
                          onToggleSelect={() => toggleEventSelection(event.id)}
                          onToggleExpand={() => setExpandedId(expandedId === event.id ? null : event.id)}
                          onToggleActive={() => toggleActive(event)}
                          onToggleCurated={() => toggleCurated(event)}
                          onStartEdit={() => startEdit(event)}
                          onSaveEdit={saveEdit}
                          onCancelEdit={() => setEditingId(null)}
                          onEditFormChange={(f) => setEditForm((prev) => ({ ...prev, ...f }))}
                          onEventRevised={(id, fields) => setEvents(prev => prev.map(e => e.id === id ? { ...e, ...fields } : e))}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ===== PENDING REVIEW TAB ===== */}
        {tab === "review" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-black tracking-tight">Pending Review</h2>
                <p className="text-sm text-muted-foreground mt-1">Scraped candidates awaiting approval. Rejected items won't be re-suggested.</p>
              </div>
              <button onClick={fetchCandidates}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground text-xs font-semibold transition-colors">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
            </div>

            {/* Sourcing toolkit — three distinct workflows */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              {/* 1) Scrape live sources */}
              <div className="bg-card rounded-2xl border border-border p-5 flex flex-col">
                <h3 className="font-bold text-foreground mb-1 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-primary" /> Discover (Scrape)
                </h3>
                <p className="text-xs text-muted-foreground mb-4">Scrape live sources (Visit Sarasota, Eventbrite, custom URLs) for new candidates.</p>
                <div className="space-y-3 flex-1">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Type</label>
                      <select value={refreshForm.searchType} onChange={e => setRefreshForm(f => ({ ...f, searchType: e.target.value as any }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs">
                        <option value="destinations">Destinations</option>
                        <option value="events">Events</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Max Results</label>
                      <input type="number" value={refreshForm.maxResults} onChange={e => setRefreshForm(f => ({ ...f, maxResults: parseInt(e.target.value) || 10 }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Radius (min)</label>
                      <input type="number" value={refreshForm.radiusMinutes} onChange={e => setRefreshForm(f => ({ ...f, radiusMinutes: parseInt(e.target.value) || 30 }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Date From</label>
                      <input type="date" value={refreshForm.dateFrom} onChange={e => setRefreshForm(f => ({ ...f, dateFrom: e.target.value }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Date To</label>
                    <input type="date" value={refreshForm.dateTo} onChange={e => setRefreshForm(f => ({ ...f, dateTo: e.target.value }))}
                      className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Source URLs (optional, one per line)</label>
                    <textarea value={refreshForm.sourceUrls} onChange={e => setRefreshForm(f => ({ ...f, sourceUrls: e.target.value }))}
                      rows={2}
                      placeholder="https://www.visitsarasota.com/events"
                      className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-[11px] font-mono" />
                  </div>
                </div>
                <button onClick={handleRefresh} disabled={scraping || seeding}
                  className="mt-4 w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50">
                  {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {scraping ? "Discovering..." : "Run Discover"}
                </button>
              </div>

              {/* 2) AI Seed (zip-aware) */}
              <div className="bg-card rounded-2xl border border-accent/40 p-5 flex flex-col">
                <h3 className="font-bold text-foreground mb-1 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent" /> AI Seed Destinations
                </h3>
                <p className="text-xs text-muted-foreground mb-4">Generate up to 10 notability-ranked permanent destinations near a ZIP. Pick tier on the next step.</p>
                <div className="space-y-3 flex-1">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Origin ZIP</label>
                      <input type="text" inputMode="numeric" maxLength={5} value={seedOriginZip}
                        onChange={(e) => setSeedOriginZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                        placeholder="34202"
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs font-mono" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Radius (min drive)</label>
                      <input type="number" min={15} max={180} value={seedRadiusMinutes}
                        onChange={(e) => setSeedRadiusMinutes(parseInt(e.target.value) || 60)}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Region label (optional)</label>
                    <input type="text" value={seedRegionLabel} onChange={(e) => setSeedRegionLabel(e.target.value)}
                      placeholder="e.g. Asheville, NC and surrounding Buncombe County"
                      className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    <p className="text-[10px] text-muted-foreground mt-1">Helps the AI orient when expanding outside Lakewood Ranch. Leave blank to default to LWR/Sarasota.</p>
                  </div>
                </div>
                <button onClick={openSeedDialog} disabled={scraping || seeding || !/^\d{5}$/.test(seedOriginZip)}
                  className="mt-4 w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-xl bg-accent text-accent-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50">
                  {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {seeding
                    ? `Seeding… ${seedElapsed}s${seedModelLabel ? ` · ${seedModelLabel}` : ""}`
                    : "Choose Tier & Seed"}
                </button>
              </div>

              {/* 3) Google Places discover */}
              <div className="bg-card rounded-2xl border border-primary/40 p-5 flex flex-col">
                <h3 className="font-bold text-foreground mb-1 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" /> Discover (Google Places)
                </h3>
                <p className="text-xs text-muted-foreground mb-4">Find well-rated, popular places near a ZIP. Filters happen at Google before AI normalization, so cost stays low.</p>
                <div className="space-y-3 flex-1">
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Search Query</label>
                    <input type="text" value={gpForm.query}
                      onChange={(e) => setGpForm(f => ({ ...f, query: e.target.value }))}
                      placeholder="e.g. kayak rentals, museums, nature preserves"
                      className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Type</label>
                      <select value={gpForm.searchType}
                        onChange={(e) => setGpForm(f => ({ ...f, searchType: e.target.value as any }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs">
                        <option value="destinations">Destinations</option>
                        <option value="events">Events</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Origin ZIP</label>
                      <input type="text" inputMode="numeric" maxLength={5} value={gpForm.zip}
                        onChange={(e) => setGpForm(f => ({ ...f, zip: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs font-mono" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Radius (min)</label>
                      <input type="number" min={5} max={120} value={gpForm.radiusMinutes}
                        onChange={(e) => setGpForm(f => ({ ...f, radiusMinutes: parseInt(e.target.value) || 30 }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Max Results</label>
                      <input type="number" min={1} max={20} value={gpForm.maxResults}
                        onChange={(e) => setGpForm(f => ({ ...f, maxResults: Math.max(1, Math.min(20, parseInt(e.target.value) || 10)) }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Min Rating</label>
                      <input type="number" step={0.1} min={0} max={5} value={gpForm.minRating}
                        onChange={(e) => setGpForm(f => ({ ...f, minRating: parseFloat(e.target.value) || 0 }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground block mb-1 uppercase">Min Reviews</label>
                      <input type="number" min={0} value={gpForm.minReviewCount}
                        onChange={(e) => setGpForm(f => ({ ...f, minReviewCount: parseInt(e.target.value) || 0 }))}
                        className="w-full rounded-lg border border-input bg-transparent px-2 py-1.5 text-xs" />
                    </div>
                  </div>
                </div>
                <button onClick={handleGooglePlacesDiscover}
                  disabled={gpDiscovering || !gpForm.query.trim() || !/^\d{5}$/.test(gpForm.zip)}
                  className="mt-4 w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50">
                  {gpDiscovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                  {gpDiscovering ? "Searching…" : "Run Google Discover"}
                </button>
              </div>

              {/* 4) Audit coverage */}
              <div className="bg-card rounded-2xl border border-border p-5 flex flex-col">
                <h3 className="font-bold text-foreground mb-1 flex items-center gap-2">
                  <Search className="h-4 w-4 text-foreground" /> Audit Coverage
                </h3>
                <p className="text-xs text-muted-foreground mb-4">Read-only check: ask AI which tier-level destinations are missing from the DB for the current origin ZIP. Use this before seeding to confirm gaps exist.</p>
                <div className="flex-1 text-xs text-muted-foreground space-y-1">
                  <div>Origin: <span className="font-mono text-foreground">{seedOriginZip}</span></div>
                  <div>Radius: <span className="text-foreground">{seedRadiusMinutes} min</span></div>
                  <div>Last tier used: <span className="text-foreground capitalize">{lastSeedTier}</span></div>
                </div>
                <button onClick={() => { setAuditResult(null); setAuditOpen(true); }} disabled={scraping || seeding || auditing}
                  className="mt-4 w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-xl border border-input bg-background text-foreground font-semibold text-sm hover:bg-muted disabled:opacity-50">
                  <Search className="h-4 w-4" /> Run Audit
                </button>
              </div>
            </div>

            {/* Bulk actions */}
            {pendingCandidates.length > 0 && (
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-muted-foreground">{selectedCandidates.size} selected</span>
                <button onClick={handleApproveSelected} disabled={selectedCandidates.size === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-highlight/10 text-highlight hover:bg-highlight/20 text-xs font-semibold disabled:opacity-30">
                  <Check className="h-3.5 w-3.5" /> Approve Selected
                </button>
                <button onClick={handleRejectSelected} disabled={selectedCandidates.size === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 text-xs font-semibold disabled:opacity-30">
                  <X className="h-3.5 w-3.5" /> Reject Selected
                </button>
                <button onClick={() => {
                  if (selectedCandidates.size === pendingCandidates.length) setSelectedCandidates(new Set());
                  else setSelectedCandidates(new Set(pendingCandidates.map(c => c.id)));
                }} className="text-xs text-primary hover:underline ml-2">
                  {selectedCandidates.size === pendingCandidates.length ? "Deselect All" : "Select All"}
                </button>
              </div>
            )}

            {/* Pending candidate list */}
            {pendingCandidates.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-2xl border border-border">
                <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No pending candidates. Run a discovery above.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingCandidates.map((c) => {
                  const d = c.data || {};
                  return (
                    <div key={c.id} className="p-4 rounded-xl border bg-card border-primary/30">
                      <div className="flex items-start gap-3">
                        <input type="checkbox" checked={selectedCandidates.has(c.id)}
                          onChange={() => toggleCandidateSelection(c.id)}
                          className="mt-1 h-4 w-4 rounded border-input" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-bold text-foreground">{c.title}</span>
                            {d.category && <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{d.category}</span>}
                            {(() => {
                              const dateLabel = formatDateBubble(d.start_date, d.end_date);
                              return dateLabel ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                                  <Calendar className="h-3 w-3" />
                                  {dateLabel}
                                </span>
                              ) : d.schedule_notes ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-accent/15 text-accent-foreground">
                                  <Clock className="h-3 w-3" />
                                  {d.schedule_notes.slice(0, 30)}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                                  Anytime
                                </span>
                              );
                            })()}
                            {/* One-click approve/reject */}
                            <div className="ml-auto flex gap-1">
                              <button onClick={() => handleQuickApprove(c.id)}
                                className="p-1 rounded-md bg-highlight/10 text-highlight hover:bg-highlight/20 transition-all" title="Approve">
                                <Check className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleQuickReject(c.id)}
                                className="p-1 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20 transition-all" title="Reject">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          {d.description && <p className="text-xs text-muted-foreground mb-1">{d.description}</p>}
                          <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground mt-1">
                            {d.official_website && (
                              <a href={d.official_website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">
                                Website <ExternalLink className="h-2.5 w-2.5" />
                              </a>
                            )}
                            {d.source_url && (
                              <a href={d.source_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:underline inline-flex items-center gap-0.5">
                                Source <ExternalLink className="h-2.5 w-2.5" />
                              </a>
                            )}
                            {d.venue_name && <span>{d.venue_name}</span>}
                            {d.cost_level && <span>{d.cost_level}</span>}
                          </div>
                          <CandidateRevisionInput candidateId={c.id} onRevised={(updatedData) => {
                            setCandidates(prev => prev.map(cc => cc.id === c.id ? { ...cc, data: updatedData } : cc));
                          }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recently Processed section */}
            {processedCandidates.length > 0 && (
              <div className="mt-6">
                <button onClick={() => setShowProcessed(!showProcessed)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
                  {showProcessed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Recently Processed ({processedCandidates.length})
                </button>
                {showProcessed && (
                  <div className="mt-3 bg-card rounded-xl border border-border divide-y divide-border">
                    {processedCandidates.map(c => (
                      <div key={c.id} className="px-4 py-2 flex items-center gap-3 text-sm">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          c.status === "approved" ? "bg-highlight/10 text-highlight" : "bg-destructive/10 text-destructive"
                        }`}>{c.status}</span>
                        <span className="font-medium truncate flex-1">{c.title}</span>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* AI Ops — score recalibration + schedule notes backfill */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-card rounded-2xl border border-border p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> AI Score Recalibration</h3>
                  <button onClick={runScoreRecalibrate} disabled={aiOpsRunning !== null}
                    className="px-3 py-1.5 rounded-lg bg-accent text-accent-foreground text-xs font-bold disabled:opacity-50">
                    {aiOpsRunning === "score" ? "Running…" : "Generate (25)"}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Re-scores novelty/intensity/weather using the rubric. Proposes changes for review.</p>
                {scoreProposals.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No pending score proposals.</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {scoreProposals.map((p) => (
                      <div key={p.id} className="text-xs border border-border rounded-lg p-2">
                        <div className="font-semibold truncate">{p.events?.title ?? p.event_id}</div>
                        <div className="text-muted-foreground mt-0.5">
                          N {p.current_novelty}→<b className="text-foreground">{p.proposed_novelty}</b> ·
                          I {p.current_intensity}→<b className="text-foreground">{p.proposed_intensity}</b> ·
                          W {p.current_weather}→<b className="text-foreground">{p.proposed_weather}</b>
                        </div>
                        <div className="text-[11px] text-muted-foreground italic mt-1">{p.rationale}</div>
                        <div className="flex gap-1 mt-1.5">
                          <button onClick={() => decideScores([p.id], "approve")} className="px-2 py-0.5 rounded bg-highlight/10 text-highlight text-[11px] font-semibold">Approve</button>
                          <button onClick={() => decideScores([p.id], "reject")} className="px-2 py-0.5 rounded bg-destructive/10 text-destructive text-[11px] font-semibold">Reject</button>
                        </div>
                      </div>
                    ))}
                    {scoreProposals.length > 1 && (
                      <button onClick={() => decideScores(scoreProposals.map((p) => p.id), "approve")}
                        className="w-full mt-1 px-3 py-1.5 rounded-lg bg-highlight/15 text-highlight text-xs font-bold">
                        Approve all {scoreProposals.length}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-card rounded-2xl border border-border p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> Backfill Schedule Notes</h3>
                  <button onClick={runNotesBackfill} disabled={aiOpsRunning !== null}
                    className="px-3 py-1.5 rounded-lg bg-accent text-accent-foreground text-xs font-bold disabled:opacity-50">
                    {aiOpsRunning === "notes" ? "Running…" : "Generate (10)"}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Looks up hours for events with no schedule notes and no start_date. Proposes notes for review.</p>
                {notesProposals.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No pending note proposals.</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {notesProposals.map((p) => (
                      <div key={p.id} className="text-xs border border-border rounded-lg p-2">
                        <div className="font-semibold truncate">{p.events?.title ?? p.event_id}</div>
                        <div className="mt-0.5"><span className="text-muted-foreground">→</span> <b className="text-foreground">{p.proposed_notes}</b></div>
                        <div className="text-[11px] text-muted-foreground italic mt-1">{p.rationale}</div>
                        <div className="flex gap-1 mt-1.5">
                          <button onClick={() => decideNotes([p.id], "approve")} className="px-2 py-0.5 rounded bg-highlight/10 text-highlight text-[11px] font-semibold">Approve</button>
                          <button onClick={() => decideNotes([p.id], "reject")} className="px-2 py-0.5 rounded bg-destructive/10 text-destructive text-[11px] font-semibold">Reject</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== FEEDBACK TAB ===== */}
        {tab === "feedback" && (
          <div>
            <h2 className="text-2xl font-black tracking-tight mb-4">Site Feedback</h2>
            {feedback.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-2xl border border-border">
                <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No feedback yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {feedback.map((fb) => (
                  <div key={fb.id} className={`p-4 rounded-xl border bg-card transition-opacity ${fb.is_read ? "border-border opacity-60" : "border-primary/30"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {fb.is_read && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                              Read
                            </span>
                          )}
                          {fb.rating === "yes" && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-highlight/10 text-highlight">
                              <ThumbsUp className="h-3 w-3" /> Helpful
                            </span>
                          )}
                          {fb.rating === "no" && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">
                              <ThumbsDown className="h-3 w-3" /> Not helpful
                            </span>
                          )}
                          {fb.plan_share_token && (
                            <a
                              href={`/plan?plan=${fb.plan_share_token}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            >
                              <Link2 className="h-3 w-3" /> View plan
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-foreground whitespace-pre-wrap">{fb.message}</p>
                        <div className="flex gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                          {fb.email && <span>From: {fb.email}</span>}
                          {fb.page_url && <span>Page: {fb.page_url}</span>}
                          <span>{new Date(fb.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => toggleFeedbackRead(fb)}
                          className={`p-2 rounded-lg transition-all ${fb.is_read ? "text-muted-foreground hover:text-foreground hover:bg-muted" : "text-highlight hover:bg-highlight/10"}`}
                          aria-label={fb.is_read ? "Mark as unread" : "Mark as read"}
                          title={fb.is_read ? "Mark as unread" : "Mark as read"}>
                          {fb.is_read ? <EyeOff className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                        </button>
                        <button onClick={() => deleteFeedback(fb.id)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                          aria-label="Delete feedback">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== EMAILS TAB ===== */}
        {tab === "emails" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-black tracking-tight">Emails</h2>
              <button onClick={fetchEmailData}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground text-xs font-semibold transition-colors">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
            </div>

            {/* Send Log */}
            <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-2">Send log (last 500, deduped)</h3>
            {emailLog.length === 0 ? (
              <div className="text-center py-10 bg-card rounded-2xl border border-border mb-8">
                <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No emails sent yet</p>
                <p className="text-xs text-muted-foreground mt-1">Share or save a plan to trigger the first email.</p>
              </div>
            ) : (
              <div className="bg-card rounded-2xl border border-border overflow-hidden mb-8">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-b border-border">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Template</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Recipient</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {emailLog.map((row) => (
                        <tr key={row.id} className="hover:bg-muted/20">
                          <td className="px-3 py-2.5">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusColor(row.status)}`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">{row.template_name}</td>
                          <td className="px-3 py-2.5 text-foreground">{row.recipient_email}</td>
                          <td className="px-3 py-2.5 text-muted-foreground text-xs whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-destructive text-xs max-w-[260px] truncate">{row.error_message || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Suppressions / Unsubscribes */}
            <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-2">Suppressed addresses</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Bounces, complaints, and unsubscribes land here. Anything in this list is blocked from receiving email.
            </p>
            <div className="flex gap-2 mb-3">
              <input
                type="email"
                value={suppressInput}
                onChange={(e) => setSuppressInput(e.target.value)}
                placeholder="Add email to suppression list"
                className="flex-1 rounded-lg border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <button
                onClick={async () => {
                  const email = suppressInput.trim().toLowerCase();
                  if (!email || !email.includes("@")) { toast.error("Invalid email"); return; }
                  try {
                    await addSuppressionFn({ data: { token: getAdminToken(), email, reason: "manual" } });
                    setSuppressInput("");
                    fetchEmailData();
                    toast.success("Suppressed");
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : "Failed");
                  }
                }}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
              >
                Suppress
              </button>
            </div>
            {suppressed.length === 0 ? (
              <div className="text-center py-8 bg-card rounded-2xl border border-border">
                <p className="text-sm text-muted-foreground">No suppressed addresses.</p>
              </div>
            ) : (
              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 border-b border-border">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Reason</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Added</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {suppressed.map((s) => (
                      <tr key={s.id} className="hover:bg-muted/20">
                        <td className="px-3 py-2.5 text-foreground">{s.email}</td>
                        <td className="px-3 py-2.5 text-muted-foreground text-xs">{s.reason}</td>
                        <td className="px-3 py-2.5 text-muted-foreground text-xs whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ===== STATS TAB ===== */}
        {tab === "stats" && (
          <div>
            <h2 className="text-2xl font-black tracking-tight mb-6">Analytics & Engagement</h2>

            {/* ===== ACTIVE USERS ===== */}
            <div className="bg-card rounded-2xl border border-border p-6 mb-6">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <BarChart3 className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-bold">Signed-in Users</h3>
                <span className="text-xs text-muted-foreground ml-auto">
                  {userStats.length} total
                  {" · "}
                  {userStats.filter((u) => Date.now() - new Date(u.last_login_at).getTime() < 7 * 24 * 60 * 60 * 1000).length} active last 7d
                  {" · "}
                  {userStats.reduce((sum, u) => sum + (u.session_count || 0), 0)} total sessions
                </span>
              </div>
              {userStats.length === 0 ? (
                <p className="text-sm text-muted-foreground">No signed-in users yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4 text-right">Sessions</th>
                        <th className="py-2 pr-4">First login</th>
                        <th className="py-2 pr-4">Last login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userStats.map((u) => (
                        <tr key={u.user_id} className="border-b border-border/50">
                          <td className="py-2 pr-4 font-medium text-foreground">{u.email}</td>
                          <td className="py-2 pr-4 text-right font-bold">{u.session_count}</td>
                          <td className="py-2 pr-4 text-muted-foreground">
                            {new Date(u.first_login_at).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric" })}
                          </td>
                          <td className="py-2 pr-4 text-muted-foreground">
                            {new Date(u.last_login_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-2xl border border-border p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-bold">Quiz Responses</h3>
                  <span className="text-xs text-muted-foreground ml-auto">{wizardResponses.length} total</span>
                </div>

                {!wizardStats ? (
                  <p className="text-sm text-muted-foreground">No quiz responses collected yet.</p>
                ) : (
                  <div className="space-y-5">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Budget vibe</p>
                      <div className="space-y-1.5">
                        {Object.entries(wizardStats.budgetVibes).map(([k, v]) => (
                          <StatBar key={k} label={k} count={v} total={wizardStats.total} color="var(--color-highlight)" />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Lead time (days from quiz → trip start)</p>
                      <div className="space-y-1.5">
                        {Object.entries(wizardStats.leadTime).map(([k, v]) => (
                          <StatBar key={k} label={k} count={v} total={wizardStats.total} color="var(--color-primary)" />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Trip length</p>
                      <div className="space-y-1.5">
                        {Object.entries(wizardStats.tripLength).map(([k, v]) => (
                          <StatBar key={k} label={k} count={v} total={wizardStats.total} color="var(--color-primary)" />
                        ))}
                      </div>
                    </div>
                    {wizardStats.topDates.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Top searched dates</p>
                        <div className="space-y-1">
                          {wizardStats.topDates.map(([d, c]) => (
                            <div key={d} className="flex items-center justify-between text-xs">
                              <span className="text-foreground">{new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
                              <span className="font-bold text-foreground">{c}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Indoor / Outdoor preference</p>
                      <div className="space-y-1.5">
                        {Object.entries(wizardStats.weatherPref).map(([k, v]) => (
                          <StatBar key={k} label={k} count={v} total={wizardStats.total} color="var(--color-highlight)" />
                        ))}
                      </div>
                    </div>
                    {Object.keys(wizardStats.durations).length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Time available</p>
                        <div className="space-y-1.5">
                          {Object.entries(wizardStats.durations).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                            <StatBar key={k} label={k} count={v} total={wizardStats.total} color="var(--color-accent)" />
                          ))}
                        </div>
                      </div>
                    )}
                    {wizardStats.topZips.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                          Top origin ZIPs {wizardStats.avgDrive != null && <span className="ml-1 text-foreground/70 normal-case">· avg max drive {wizardStats.avgDrive} min</span>}
                        </p>
                        <div className="space-y-1">
                          {wizardStats.topZips.map(([z, c]) => (
                            <div key={z} className="flex items-center justify-between text-xs">
                              <span className="text-foreground font-mono">{z}</span>
                              <span className="font-bold text-foreground">{c}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">"Anything else" notes</p>
                      <p className="text-xs text-muted-foreground">
                        {wizardStats.extrasFilled} of {wizardStats.total} ({Math.round((wizardStats.extrasFilled / wizardStats.total) * 100)}%) filled it · avg {wizardStats.avgExtrasLen} chars
                      </p>
                      {(wizardStats.topBoost.length > 0 || wizardStats.topPenalize.length > 0) && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <p className="text-[10px] font-semibold text-highlight uppercase mb-1">Top boosted</p>
                            <div className="space-y-0.5">
                              {wizardStats.topBoost.map(([k, c]) => (
                                <div key={k} className="flex justify-between text-xs"><span className="truncate pr-1">{k}</span><span className="font-bold">{c}</span></div>
                              ))}
                              {wizardStats.topBoost.length === 0 && <p className="text-xs text-muted-foreground italic">none</p>}
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-destructive uppercase mb-1">Top avoided</p>
                            <div className="space-y-0.5">
                              {wizardStats.topPenalize.map(([k, c]) => (
                                <div key={k} className="flex justify-between text-xs"><span className="truncate pr-1">{k}</span><span className="font-bold">{c}</span></div>
                              ))}
                              {wizardStats.topPenalize.length === 0 && <p className="text-xs text-muted-foreground italic">none</p>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {wizardStats.passCount} of {wizardStats.total} users ({Math.round((wizardStats.passCount / wizardStats.total) * 100)}%) mention local passes/memberships
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="bg-card rounded-2xl border border-border p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="h-5 w-5 text-highlight" />
                    <h3 className="text-lg font-bold">Most Saved Events</h3>
                  </div>
                  {topSavedEvents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No saves recorded yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {topSavedEvents.map((e, i) => (
                        <div key={e.id} className="flex items-center gap-3 text-sm">
                          <span className="text-xs font-black text-muted-foreground w-5">{i + 1}</span>
                          <span className="flex-1 truncate font-medium">{e.title}</span>
                          <span className="text-xs font-bold text-highlight bg-highlight/10 px-2 py-0.5 rounded-full">{e.save_count} saves</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-card rounded-2xl border border-border p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <ExternalLink className="h-5 w-5 text-accent" />
                    <h3 className="text-lg font-bold">Most Clicked Links</h3>
                  </div>
                  {topClickedEvents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No link clicks recorded yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {topClickedEvents.map((e, i) => (
                        <div key={e.id} className="flex items-center gap-3 text-sm">
                          <span className="text-xs font-black text-muted-foreground w-5">{i + 1}</span>
                          <span className="flex-1 truncate font-medium">{e.title}</span>
                          <span className="text-xs font-bold text-accent bg-accent/10 px-2 py-0.5 rounded-full">{e.link_click_count} clicks</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== SYSTEM REFERENCE TAB ===== */}
        {tab === "reference" && <SystemReference />}
      </div>

      {/* ===== Tier choice dialog ===== */}
      {tierDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setTierDialogOpen(false)}>
          <div className="bg-background border border-border rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1">Run AI Seed — choose tier</h2>
            <p className="text-xs text-muted-foreground mb-1">
              Current destinations: <span className="font-semibold text-foreground">{seedContext?.totalDestinations ?? "…"}</span>
              {" · "}Last seed tier: <span className="font-semibold text-foreground">{lastSeedTier}</span>
            </p>
            <p className="text-xs text-muted-foreground mb-1">
              Origin ZIP: <span className="font-mono font-semibold text-foreground">{seedOriginZip}</span>
              {" · "}Radius: <span className="font-semibold text-foreground">{seedRadiusMinutes} min</span>
              {seedRegionLabel && <> {" · "}<span className="text-foreground">{seedRegionLabel}</span></>}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Pick the tier of destinations you want this run to focus on. Move down a tier when results start coming back as duplicates.
            </p>
            <div className="space-y-2">
              {([
                { tier: "flagship" as SeedTier, label: "Flagship", desc: "Top-of-mind anchors everyone hears about first." },
                { tier: "notable" as SeedTier, label: "Notable", desc: "Solid second-tier picks locals & visitors recommend." },
                { tier: "niche" as SeedTier, label: "Niche", desc: "Hidden gems, locals' favorites, specialty venues." },
                { tier: "deep-cut" as SeedTier, label: "Deep-cut", desc: "Long-tail / very specific-interest. Quality over quantity." },
              ]).map(({ tier, label, desc }) => (
                <button
                  key={tier}
                  onClick={() => handleAISeed(tier)}
                  className={`w-full text-left p-3 rounded-xl border transition hover:bg-muted ${tier === lastSeedTier ? "border-accent bg-accent/5" : "border-input"}`}
                >
                  <div className="font-semibold text-sm">{label}{tier === lastSeedTier && <span className="text-xs font-normal text-muted-foreground"> (last used)</span>}</div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                </button>
              ))}
            </div>
            <label className="mt-4 flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={useProModel}
                onChange={(e) => setUseProModel(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <span>
                High-quality (<span className="font-mono">gemini-2.5-pro</span>, slower) — default off uses faster Flash model
              </span>
            </label>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setTierDialogOpen(false)} className="text-xs px-3 py-1.5 rounded-lg text-muted-foreground hover:text-foreground">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Audit coverage dialog ===== */}
      {auditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !auditing && setAuditOpen(false)}>
          <div className="bg-background border border-border rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1">Audit tier coverage</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Ask AI to identify any destinations at the chosen tier that are MISSING from the DB. Read-only — no inserts.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {(["flagship", "notable", "niche", "deep-cut"] as SeedTier[]).map((t) => (
                <button
                  key={t}
                  onClick={() => runAudit(t)}
                  disabled={auditing}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition disabled:opacity-50 ${auditTier === t ? "border-accent bg-accent/10" : "border-input hover:bg-muted"}`}
                >
                  {t}
                </button>
              ))}
            </div>
            {auditing && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin" /> Running audit for "{auditTier}" tier...
              </div>
            )}
            {!auditing && auditResult && (
              <div>
                {auditResult.success ? (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Tier: <span className="font-semibold text-foreground">{auditResult.tier}</span>
                      {" · "}Existing destinations: {auditResult.totalDestinations}
                    </p>
                    {auditResult.summary && (
                      <p className="text-sm mb-3 italic">"{auditResult.summary}"</p>
                    )}
                    {auditResult.missing.length === 0 ? (
                      <div className="p-3 rounded-lg bg-highlight/10 text-highlight text-sm font-semibold">
                        ✓ Coverage looks complete — no missing {auditResult.tier}-tier destinations identified.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-sm font-semibold">{auditResult.missing.length} potentially missing:</div>
                        {auditResult.missing.map((m, i) => (
                          <div key={i} className="p-2.5 rounded-lg border border-input">
                            <div className="font-semibold text-sm">{m.name}</div>
                            <div className="text-xs text-muted-foreground">{m.why}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                    Audit failed: {auditResult.error}
                  </div>
                )}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button onClick={() => setAuditOpen(false)} disabled={auditing} className="text-xs px-3 py-1.5 rounded-lg text-muted-foreground hover:text-foreground disabled:opacity-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Bulk delete confirmation dialog ===== */}
      {bulkDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>
          <div className="bg-background border border-border rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-full bg-destructive/10 p-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground">Delete {selectedEventIds.size} event{selectedEventIds.size === 1 ? "" : "s"}?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This permanently removes the selected event{selectedEventIds.size === 1 ? "" : "s"} from the database. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setBulkDeleteOpen(false)}
                disabled={bulkDeleting}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete {selectedEventIds.size}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== System Reference Panel =====

function SystemReference() {
  const [openSection, setOpenSection] = useState<number | null>(null);
  const toggle = (n: number) => setOpenSection(openSection === n ? null : n);

  const sections = [
    {
      title: "1. Data Sourcing & Validation",
      content: `Three discovery paths feed the **scrape_candidates** queue. All three run from the admin Pending Review tab.

**1. Refresh from URLs (Firecrawl + AI)** — discovery_source: \`ai_scrape\`
- Admin pastes one or more source URLs (Visit Sarasota, Eventbrite, county pages, venue pages, etc.). There is no hardcoded aggregator list.
- Firecrawl fetches each URL as markdown, then Gemini 2.5 Flash extracts structured candidate JSON for the chosen type (destination or event).
- If \`FIRECRAWL_API_KEY\` is not set OR no URLs are provided, the AI falls back to generating candidates from its own training data (lower quality, still flagged \`ai_scrape\`).

**2. Discover (Google Places)** — discovery_source: \`google_places\`
- Admin enters a search query + origin ZIP + radius/rating/review filters. Origin is geocoded via the \`zip_cache\` table first (no Google call on a repeat ZIP).
- One Places API (New) Text Search call with a tight field mask (~$0.032/run) returns up to 20 results, sorted server-side by \`rankPreference: POPULARITY\` and filtered by min rating + min review count.
- Results are re-sorted client-side by \`rating × log10(userRatingCount + 10)\` ("worthiness"), then normalized by Gemini 2.5 Flash into candidate JSON. Place Details is only called on demand per row.

**3. Community / manual submissions** — discovery_source: free-text (e.g. \`manual\`)
- Admin can paste a single candidate directly. Same downstream pipeline.

**Dedup (applies to all paths):**
- Fuzzy title + venue match against existing \`events\` AND already-rejected \`scrape_candidates\` (Sources 1 & 2 both check rejected history).
- The matcher uses normalized substring containment **plus** word-token Jaccard similarity > 0.6 (words ≤ 2 chars are dropped).
- Google Places adds an extra exact-match check on \`google_place_id\`.

**Learning loop (Source 1 only):** The Firecrawl/AI prompt is fed the last batch of approved and rejected candidates so the model picks up what "worthy" means in this region over time. Google Places normalization does not currently consume this history.

**Verification (manual review):** Every candidate lands in Pending Review with status \`pending\`. The admin still has to confirm address, venue, and source URL before approving — none of the discovery paths auto-publish.`
    },
    {
      title: "2. Worthiness Criteria",
      content: `An experience is worthy of the database if it meets ALL of these:

✅ **Leisure/recreation focus** — not retail, restaurants, grocery, or services
✅ **Unique features** — parks must have distinguishing features beyond grass and benches (e.g., splash pad, nature trail, disc golf)
✅ **Verifiable** — must be a real place/event with a real address and ideally a source URL
✅ **Current** — events must be upcoming or recurring; expired one-off events get deactivated
✅ **Novelty ≥ 4** — drum circles and similar low-novelty activities score ≤4 and may be excluded
✅ **Materially unique** — Marie Selby Gardens Downtown vs. Spanish Point = distinct entries; butterfly garden within Selby = NOT a separate listing
✅ **No hallucinated or conflated entries** — AI-generated candidates must be verified before approval

**Exclusions:**
- Standalone restaurants, bars, breweries, grocery stores
- Retail malls or outlet centers
- Standalone movie theaters
- Generic neighborhood parks without unique features`
    },
    {
      title: "3. Required Data Fields",
      content: `**Mandatory fields** (must be populated for every entry):
- title, description, category, venue_name, venue_address, cost_level, indoor_outdoor
- Description hard limit: \`DESCRIPTION_TARGET_WORDS\` words (\`DESCRIPTION_MAX_WORDS\` absolute max). Forbidden phrases include "rainy day" / "rain or shine" — the indoor/outdoor bubble already conveys weather fit.

**Date/Schedule model (3 fields):**
- start_date (DATE, nullable): first/only date. NULL = recurring or permanent venue.
- end_date (DATE, nullable): last date for multi-day. NULL or same as start_date = single-day.
- schedule_notes (TEXT): free-text for recurring patterns ("Saturdays 9am-1pm", "Every Tuesday", "Weekends only") or extra context. Also used for events with no specific dates. **Specific play-day list:** for game-day / show-only venues, paste 2+ ISO dates ("Game days: 2026-02-22, 2026-02-25, 2026-02-28") and the results filter will only show the venue on those days inside the user's selected window.

**Recurring detection:** if start_date is NULL and schedule_notes is non-empty → recurring event. If both are NULL → anytime/permanent.

**AI-scored at scrape time** (static attributes):
- novelty_score (1-10): how unique/special the experience is
- intensity (1-10): physical activity level (seated/passive like film festivals = 1-2)
- weather_sensitivity (0-100): 0 = weatherproof, 100 = very weather dependent
- crowd_level: Low / Medium / High
- See §7 (AI Score Rubric) for the 1 / 5 / 10 anchor definitions used to keep these scores calibrated.

**Calculated at scrape time:**
- drive_minutes_from_lwr: estimated from Lakewood Ranch (34202)
- cost_level / cost_details
- schedule_notes

**Determined real-time during recommendation:**
- Age-appropriateness: inferred from category, intensity, and description keywords (NOT stored as a DB field)
- Weather impact (live weather data)
- Date/day filtering (uses start_date/end_date range overlap + schedule_notes day-name matching)
- Match score against quiz preferences
- Popularity boost from save_count and link_click_count

**Legacy field (still in DB, no longer used in scoring):**
- age_range: retained for reference but scoring now uses real-time inference`
    },
    {
      title: "4. Scoring & Calculation Timing",
      content: `**At scrape/refresh time** (stored in DB, updated on re-scrape):
- novelty_score, intensity, crowd_level, weather_sensitivity
- cost_level, cost_details, drive_minutes_from_lwr
- indoor_outdoor, estimated_duration, schedule_notes
- start_date, end_date (parsed from AI date strings; ranges like "2026-04-15 to 2026-04-19" → start=Apr 15, end=Apr 19)

**Real-time at query** (calculated when user loads results):
- Weather impact score: fetched from live weather API, combined with event's weather_sensitivity AND the wizard weather-exposure preference (−2…+2)
- Date/day filtering: uses range overlap — event shown if [start_date, end_date] overlaps user's selected dates; for recurring, schedule_notes is checked for matching day names. **All date math is anchored to America/New_York (EST/EDT)** so "tomorrow" / "this weekend" reflect local Florida days, not the visitor's browser timezone.
- Age-appropriateness: inferred from category + intensity + description keywords (replaces stored age_range)
- Quiz match score: budget fit, indoor/outdoor preference, intensity preference, free-text keyword matching

**Accumulated over time** (user engagement signals):
- save_count: incremented per unique session save → contributes to popularity boost (cap depends on mode, see §5)
- link_click_count: incremented per unique session click → contributes to popularity boost (cap depends on mode, see §5)`
    },
    {
      title: "5. Recommendation & Ranking Logic",
      content: `**Two ranking modes** sit on the same scoring engine but use different weights so the user can either get a confident match or genuine variety. All math lives in \`src/routes/explore.tsx\` inside the sort comparator.

**Smart Ranked** (default after wizard; auto-engages whenever any filter chip or search query is active):
- Base score = **novelty_score × 5**
- Weather impact (when forecast is loaded): pop-bucketed against indoor/outdoor + a sensitivity curve.
  - Rain probability ≥85%: indoor **+22**, outdoor **−6**, plus an outdoor sensitivity penalty up to ~**−25** at sensitivity 100.
  - ≥70%: indoor **+14**, outdoor **−3**, sens penalty up to ~**−16**.
  - ≥50%: indoor **+6**, outdoor **−1**, sens penalty up to ~**−8**. "Both/mixed" venues get half the indoor boost and ~35% of the sensitivity penalty.
  - <30%: outdoor **+6**.
- Wizard weather-exposure preference (−2…+2): linear weight **|pref| × 8** (so 8 / 16 at the extremes), applied for/against indoor vs. outdoor, with a small extra ±half-weight scaled by weather_sensitivity.
- Intensity match: **−4 per point** of difference from the inferred preferred intensity.
- Popularity boost: **+min(save_count × 2, 10)** and **+min(link_click_count × 0.5, 5)**.
- Cost preference (only when costFilter is "free" or "under25"): **+15** for $0 events, then **−0.3 × cost**.
- Drive penalty: linear **−0.3 / min** up to 45 min, then quadratic ramp \`−0.4 × (m − 45)^1.5\` so a 90-min drive really stings.
- Drive-aware duration nudge: if round-trip drive + venue duration overshoots the chosen Time-Available bucket, **−4** (Whole day) or **−8** (Under 2 hrs / Half day). The bucket itself is still a hard filter on venue duration alone.
- Timeliness (dated events): \`base × scarcity × noveltyMod + 1-day bonus\`.
  - base: in-window **22** (Smart) / **30** (Shuffle); dated-but-out-of-window **4** / **6**.
  - scarcity: **1.0** (≤14 day run), 0.6 (≤30), 0.35 (≤90), 0.15 (longer).
  - noveltyMod: **0.5 + novelty_score / 20** (range 0.55–1.0).
  - +**6** extra when an in-window event runs ≤1 day (or has exactly one in-window play date).
- Free-text wizard signals: **+25** per matched boost keyword, **−30** per penalize keyword, **±15/−25** on category boosts, **+10** for indoor/outdoor preference, **+8** per local-passes membership keyword, **+20** when a venue name matches a reciprocal pass.
- Hot-day shuffle bonus only: when avg forecast temp >85°F, indoor or water-category events get **+15**.

**Shake It Up** (default when there is no wizard data; tapping it also clears the filter chips and search box, but keeps timeframe + wizard influence intact):
- Base score = **novelty_score × 8** (heavier weight on novelty).
- Popularity caps lowered: **+min(save_count × 1, 4)** and **+min(link_click_count × 0.25, 2)** so a single high-save venue doesn't always lead.
- Timeliness uses the bigger Shuffle base values shown above.
- Per-event randomization of **±25**, seeded by a fresh random value each time the button is tapped (stable within a single shake).
- All other rules (weather, exposure preference, intensity, cost, distance, keywords, passes) match Smart Ranked.

**Hard filters applied to BOTH modes:**
- Only active events (\`is_active = true\`).
- **$150 cost cap** on min(parseCostNumeric(cost_level, cost_details)). Cost \`999\` (= "unknown / no number") is exempted.
- Wizard \`maxDriveMinutes\` is a hard cap on \`drive_minutes_from_lwr\` unless the user explicitly clicks "show beyond cap".
- "Free" cost filter excludes any event whose cost_details contains "$" UNLESS the wording explicitly carves out free entry/beach/grounds/admission (lets kayak-rental venues with a free beach still appear).
- "Under $25" filter caps cost similarly.
- Time Available bucket (wizard or filter bar): "Under 2 hrs" (≤ 2 hr), "Half day" (2–5 hr), "Whole day" (≥ 5 hr OR text matching "all day / whole day / multiple days"), "Show all" (any). Parser: \`src/lib/wizard-types.ts\` → \`parseDurationHours\` / \`matchesDurationPreference\`.
- Date / day filtering: range overlap on start_date/end_date + day-name match in schedule_notes + specific-date-list match — all anchored to America/New_York.
- Free-text search: case-insensitive substring across title + venue_name + category + description.
- **Max 30 results returned.**`
    },
    {
      title: "6. Data Utilization Audit",
      content: `**Currently used in ranking/filtering:**
- budgetLevel (from wizard) → drives cost-filter application; cost preference math is in §5
- duration (from wizard / filter bar) → hard filter via \`matchesDurationPreference\` AND a soft drive-aware overshoot penalty
- weatherExposure (−2…+2 from wizard) → indoor/outdoor weighting
- hasLocalPasses + reciprocal venue list → +8 / +20 boosts
- timeframe_label / custom date range → date/day filtering and the in-window timeliness boost
- origin.zip + origin.maxDriveMinutes (wizard) → distance recompute and hard drive cap
- Live weather (precipChance, avg temp) → weather impact and hot-day shuffle bonus
- save_count → popularity boost (Smart: +2/save, cap +10 · Shuffle: +1/save, cap +4)
- link_click_count → popularity boost (Smart: +0.5/click, cap +5 · Shuffle: +0.25/click, cap +2)
- Free-text search query → soft scoring signal extraction (boost / penalize keywords) plus the substring filter

**Collected but not used in scoring:**
- session_id — used for save / click de-dup, never for personalization
- google_places_cache rows beyond what feeds normalization (rating, photos, hours, etc. are stored for future use)

**Removed from wizard:**
- group_size — did not influence scoring; removed

**Removed from schema (consolidated):**
- date_specific → replaced by start_date
- day_of_week → merged into schedule_notes
- is_recurring → inferred from start_date NULL + schedule_notes non-empty

**Legacy / advisory fields:**
- age_range (DB column) — retained for human reference; scoring uses real-time inference
- crowd_level — surfaced in the UI but not currently a ranking input`
    },
    {
      title: "7. AI Score Rubric (1 / 5 / 10 anchors)",
      content: `These anchors are what the AI prompt should target when assigning scores at scrape, Google Places normalization, and proposal re-scoring time. Compressed scores (everything is a 7) destroy ranking signal — keep the curve wide.

**novelty_score (1–10)** — uniqueness in the region. Drives the base ranking weight (×5 Smart, ×8 Shuffle) AND the timeliness noveltyMod.
- **10** — One-of-a-kind, headline-worthy. Myakka Canopy Walkway, Bok Tower carillon recital.
- **8–9** — Strong story, destination-grade. Selby Gardens, Ringling, helicopter tour, TreeUmph.
- **6–7** — Solid local attraction worth a dedicated trip. Mid-size museums, named beaches, guided kayak/eco tours.
- **4–5** — Generic-but-pleasant. Stroll-a-street, neighborhood park, basic bowling alley.
- **1–3** — Commodity. Plain pocket park, chain arcade, undifferentiated venue.
- **Calibration:** a normal city park is 5, not 7. Reserve 8+ for genuinely rare assets. Drum circles cap at 4.

**intensity (1–10)** — physical exertion required (not skill or danger).
- **9–10** — Endurance / extreme. Long hikes, multi-hour open-water kayak.
- **7–8** — High effort, sweaty. TreeUmph ropes course, jet ski, trampoline park.
- **5–6** — Moderate, steady movement. Bike trails, paddleboard, longer walking tours.
- **3–4** — Light, casual. Garden stroll, beach time, easy preserve walk.
- **1–2** — Sedentary / passive. Museum, theater, comedy, painting class, film festival.
- **Hard rule:** seated/passive experiences are 1–2, never 3+.

**weather_sensitivity (0–100)** — how badly bad weather hurts the experience. Drives the weather-impact penalty curve.
- **85–100** — Ruined by rain or extreme heat. Beach swim, parasail, open kayak, jet ski.
- **60–80** — Significantly hurt. Open-air zoo/garden, outdoor festival, uncovered preserve hike.
- **40–55** — Mixed. Walking village/main street, partly covered market, brewery patio.
- **20–35** — Mostly fine. Covered pavilion, museum with grounds, indoor with outdoor element.
- **0–15** — Weatherproof. Indoor museum, theater, bowling, escape room.
- **Hard rule:** if \`indoor_outdoor === "indoor"\`, weather_sensitivity must be **0** (the proposal pipeline already enforces this).

**crowd_level** — the typical experience, not worst-case holidays. Surfaced in the UI; not currently a scoring input.
- **Low** — usually quiet, easy parking, no lines.
- **Medium** — busy at peak hours/weekends, generally manageable.
- **High** — expect lines, parking pressure, crowd management on most visits.

**cost_level / cost_details** — short price string from a fixed set: \`Free\`, \`$<N>\`, \`$<N>–$<M>\`, \`$<N> per person\`, \`$<N> per ticket\`, \`$<N> per adult\`, \`$<N> per vehicle\`. Pick the most representative single price; do not list every tier. If genuinely free, both fields must be exactly \`Free\`. Forbidden hedge words in cost_details: "donation", "appreciated", "approximately", "vary", "vendor", "extra", "starting at", "around", "suggested", "no entry fee", "fees".

**age_range** — DB field retained for human reference but **no longer used in scoring** (real-time inference from category + intensity + keywords replaced it). Don't waste time tuning this; it doesn't affect ranking.

**Keep the rubric and the live AI prompts aligned.** If a prompt's scoring guidance changes in code (\`scrape.functions.ts\`, \`google-places.functions.ts\`, \`proposals.functions.ts\`), update this section in the same change so the curve stays calibrated.`
    },
    {
      title: "8. URL & Link Fields (venue link vs. map override vs. official website vs. source)",
      content: `Four URL-ish fields exist on every event. They look similar but serve different jobs. The AI revise prompt in \`scrape.functions.ts\` uses these definitions to route edits to the right field — keep this section and that prompt in sync.

**venue_name + venue_address** — the physical place.
- These two fields are the **default source** for the Google Maps link the card title points to, in both the user-facing EventCard (Explore + Plan) and the admin Venue column. The default formula is \`https://www.google.com/maps/search/?api=1&query=<venue_name> <venue_address>\`.
- This is a free-text Maps **search**, not a place ID, so when multiple Google listings share an address (parks with concessions inside, malls with anchor stores, etc.) Google may pick the wrong pin. In those cases use \`map_url\` (below) to override.
- Also drives distance-from-ZIP and the admin free-text search.

**map_url** — optional override for the title link.
- When non-empty, the EventCard title and the admin Venue column link to this URL **verbatim** instead of the constructed Google Maps search. Best filled with a "Share → Copy link" URL from Google Maps (e.g. \`https://maps.app.goo.gl/...\`) or a full \`maps/place/?q=place_id:…\` URL.
- Leave blank when the default search resolves correctly. Only set this to fix mismatched pins for the title link — it does not affect distance, search, or scraping.
- The admin Venue column shows a 📍 marker when \`map_url\` is set so curators can see at a glance which rows have an override.

**official_website** — the venue's own homepage (e.g. \`myakkariver.org\`, \`ringling.org\`).
- This is the canonical "this venue's URL". It is the **primary URL the schedule-refresh proposal job re-scrapes** to detect new dates and updated hours (\`proposals.functions.ts\`).
- Not currently surfaced on the user-facing card. It is shown in the admin row as a hostname link.
- Edits triggered by phrases like "official website", "their site", "homepage" should land here — never on \`venue_address\`.

**source_url (+ source_name)** — provenance / fallback.
- Where the listing originally came from: Visit Sarasota, Eventbrite, a Google Places \`maps/place/?q=place_id:…\` URL, an AI scrape source, etc. \`source_name\` is the human label ("Visit Sarasota", "Google Places", "AI Seed (premium)").
- Auto-populated by the discovery pipeline (\`google-places.functions.ts\`, \`scrape.functions.ts\`, \`seed-destinations.functions.ts\`). Rarely hand-edited.
- Used as the **fallback scrape target** in the proposals job when \`official_website\` is empty, and as an audit trail in the admin row. Not shown to end users.
- Edits triggered by phrases like "source", "where this came from", "scrape source" should land here.

**Disambiguation rule for AI revise** (mirrors the prompt in \`scrape.functions.ts\`):
- "venue link" / "venue location link" / "map link" / "the link on the title" / "wrong listing" / "wrong pin" + a Maps URL pasted by the admin → set \`map_url\` to the pasted URL. Only edit \`venue_name\`/\`venue_address\` if the physical place itself is wrong (different address, renamed venue).
- "official website" / "their site" / "homepage" → \`official_website\`.
- "source" / "where this came from" / "scrape source" → \`source_url\` (and optionally \`source_name\`).

**Field hygiene:**
- \`venue_name\` and \`venue_address\` are mandatory and should be specific enough that the Google Maps query lands on the right pin (street + city, not just "Lakewood Ranch").
- \`map_url\` is optional. Only fill it when the default search resolves to the wrong listing.
- \`official_website\` is strongly recommended. Without it, the schedule-refresh proposal job has nothing reliable to re-scrape (aggregator pages like Eventbrite rot fast).
- \`source_url\` is optional in practice; keep it for provenance but don't curate it by hand.`
    },
  ];

  return (
    <div>
      <h2 className="text-2xl font-black tracking-tight mb-2">System Reference</h2>
      <p className="text-sm text-muted-foreground mb-6">Business logic documentation. Update these as the system evolves.</p>

      <div className="space-y-3">
        {sections.map((sec, i) => (
          <div key={i} className="bg-card rounded-2xl border border-border overflow-hidden">
            <button onClick={() => toggle(i)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/20 transition-colors">
              <span className="font-bold text-foreground">{sec.title}</span>
              {openSection === i ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {openSection === i && (
              <div className="px-6 pb-6 text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                {sec.content.split(/\*\*(.*?)\*\*/g).map((part, pi) =>
                  pi % 2 === 1 ? <strong key={pi} className="text-foreground">{part}</strong> : <span key={pi}>{part}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Condensed Event Row Component =====

function EventRow({
  event,
  expanded,
  editing,
  editForm,
  selected,
  onToggleSelect,
  onToggleExpand,
  onToggleActive,
  onToggleCurated,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditFormChange,
  onEventRevised,
}: {
  event: DBEvent;
  expanded: boolean;
  editing: boolean;
  editForm: Partial<DBEvent>;
  selected: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onToggleActive: () => void;
  onToggleCurated: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditFormChange: (f: Partial<DBEvent>) => void;
  onEventRevised: (id: string, fields: Partial<DBEvent>) => void;
}) {
  return (
    <>
      <tr
        className={`hover:bg-muted/20 cursor-pointer transition-colors ${!event.is_active ? "opacity-50" : ""} ${selected ? "bg-primary/5" : ""}`}
        onClick={onToggleExpand}
      >
        <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`Select ${event.title}`}
            className="h-3.5 w-3.5 rounded border-input cursor-pointer"
          />
        </td>
        <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
          <button onClick={onToggleActive}
            className={`p-1 rounded-md transition-all ${event.is_active ? "bg-highlight/20 text-highlight hover:bg-highlight/30" : "bg-destructive/10 text-destructive hover:bg-destructive/20"}`}
            aria-label={event.is_active ? "Disable event" : "Enable event"}>
            {event.is_active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          </button>
        </td>
        <td className="px-2 py-1.5 font-medium max-w-[220px] truncate text-xs">{event.title}</td>
        <td className="px-2 py-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">{event.category}</span>
        </td>
        <td className="px-2 py-1.5 max-w-[150px] truncate text-xs" onClick={(e) => e.stopPropagation()}>
          {event.venue_name ? (
            <a
              href={
                event.map_url && event.map_url.trim().length > 0
                  ? event.map_url
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue_name + " " + (event.venue_address || ""))}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary hover:underline inline-flex items-center gap-0.5"
              title={
                event.map_url && event.map_url.trim().length > 0
                  ? "Pinned Google Maps listing (map_url override — matches the event card title link)"
                  : "Google Maps search from venue_name + venue_address (matches the event card title link)"
              }
            >
              <span className="truncate">{event.venue_name}</span>
              {event.map_url && event.map_url.trim().length > 0 && (
                <span className="text-primary text-[9px] font-bold leading-none" aria-label="Pinned map URL">📍</span>
              )}
              <ExternalLink className="h-2.5 w-2.5 shrink-0" />
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-2 py-1.5 text-[11px] max-w-[170px]">
          <div className="flex items-center gap-1.5 flex-wrap">
            {getEventType(event) === "limited" && (
              <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-full whitespace-nowrap">Limited</span>
            )}
            <span className="text-muted-foreground truncate">{formatSchedule(event)}</span>
          </div>
        </td>
        <td className="px-2 py-1.5 text-muted-foreground text-xs">{event.cost_details || event.cost_level}</td>
        <td className="px-2 py-1.5 text-muted-foreground text-[11px] whitespace-nowrap">
          {event.created_at ? new Date(event.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
        </td>
        <td className="px-2 py-1.5 text-center text-xs font-bold">{event.novelty_score}</td>
        <td className="px-2 py-1.5 text-center text-xs text-muted-foreground">{event.intensity}</td>
        <td className="px-2 py-1.5 text-center text-xs">
          <span className="font-bold text-highlight">{event.save_count || 0}</span>
          <span className="text-muted-foreground">/</span>
          <span className="font-bold text-accent">{event.link_click_count || 0}</span>
        </td>
        <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onToggleCurated}
            className={`p-1 rounded-md transition-all ${event.is_curated ? "text-highlight hover:bg-highlight/15" : "text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted"}`}
            aria-label={event.is_curated ? "Un-curate" : "Mark as human-curated"}
            title={event.is_curated ? `Curated${event.curated_at ? ` ${new Date(event.curated_at).toLocaleDateString()}` : ""} — click to remove` : "Mark as human-curated (feeds AI learning context)"}
          >
            <Star className={`h-3.5 w-3.5 ${event.is_curated ? "fill-current" : ""}`} />
          </button>
        </td>
        <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
          <button onClick={onStartEdit}
            className="p-1 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all">
            <Edit2 className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {expanded && editing && (
        <tr className="bg-primary/5">
          <td colSpan={13} className="px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {(["title", "description", "category", "venue_name", "venue_address", "map_url", "schedule_notes", "cost_level", "cost_details", "indoor_outdoor", "age_range", "crowd_level", "estimated_duration", "source_url", "official_website", "curated_notes", "region_tag"] as const).map((field) => (
                <div key={field}>
                  <label className="font-semibold text-muted-foreground block mb-1 capitalize">{field.replace(/_/g, " ")}</label>
                  <input type="text" value={(editForm as any)[field] || ""}
                    onChange={(e) => onEditFormChange({ [field]: e.target.value })}
                    className="w-full rounded border border-input bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary/20" />
                  {field === "schedule_notes" && (
                    <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                      Game-day or show-only venues: paste specific dates here as <code>YYYY-MM-DD</code> (comma- or space-separated). When 2+ dates are present, they become the authoritative play-day list and the event will only show on those days.
                    </p>
                  )}
                  {field === "map_url" && (
                    <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                      Optional. Paste a <code>maps.app.goo.gl/...</code> share link to pin the exact Google Maps listing the card title should open. Leave blank to use the default <code>venue_name + venue_address</code> search.
                    </p>
                  )}
                  {field === "curated_notes" && (
                    <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                      Optional. One-line note about <em>why</em> this is a gold example (e.g. "best Nature pick — quiet, novel"). Injected into the AI learning context next to the event.
                    </p>
                  )}
                  {field === "region_tag" && (
                    <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                      Free-form tag like <code>core</code>, <code>tampa</code>, <code>stpete</code>. Used for future per-region scoping of curated examples.
                    </p>
                  )}
                </div>
              ))}
              {(["start_date", "end_date"] as const).map((field) => (
                <div key={field}>
                  <label className="font-semibold text-muted-foreground block mb-1 capitalize">{field.replace(/_/g, " ")}</label>
                  <input type="date" value={(editForm as any)[field] || ""}
                    onChange={(e) => onEditFormChange({ [field]: e.target.value || null })}
                    className="w-full rounded border border-input bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary/20" />
                </div>
              ))}
              {(["novelty_score", "intensity", "weather_sensitivity", "drive_minutes_from_lwr"] as const).map((field) => (
                <div key={field}>
                  <label className="font-semibold text-muted-foreground block mb-1 capitalize">{field.replace(/_/g, " ")}</label>
                  <input type="number" value={(editForm as any)[field] ?? ""}
                    onChange={(e) => onEditFormChange({ [field]: parseInt(e.target.value, 10) || 0 })}
                    className="w-full rounded border border-input bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary/20" />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={onSaveEdit} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90">
                <Check className="h-3 w-3" /> Save
              </button>
              <button onClick={onCancelEdit} className="px-3 py-1.5 rounded-lg border border-border text-xs hover:bg-muted transition-colors">
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
      {expanded && !editing && (
        <tr className="bg-muted/10">
          <td colSpan={13} className="px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="font-semibold text-muted-foreground block mb-1">Description</span>
                <p className="text-foreground">{event.description}</p>
              </div>
              <div>
                <span className="font-semibold text-muted-foreground block mb-1">Address</span>
                <p>{event.venue_address || "—"}</p>
                <span className="font-semibold text-muted-foreground block mb-1 mt-2">Age Range</span>
                <p>{event.age_range}</p>
              </div>
              <div>
                <span className="font-semibold text-muted-foreground block mb-1">Schedule Notes</span>
                <p>{event.schedule_notes || "—"}</p>
                <span className="font-semibold text-muted-foreground block mb-1 mt-2">Intensity</span>
                <p>{event.intensity}/10</p>
              </div>
              <div>
                <span className="font-semibold text-muted-foreground block mb-1">Source</span>
                {event.source_url ? (
                  <a href={event.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                    {event.source_name || "Link"} <ExternalLink className="h-3 w-3" />
                  </a>
                ) : <p>—</p>}
                {event.official_website && (
                  <>
                    <span className="font-semibold text-muted-foreground block mb-1 mt-2">Official Website</span>
                    <a href={event.official_website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      {(() => { try { return new URL(event.official_website).hostname.replace("www.", ""); } catch { return event.official_website; } })()} <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                )}
                {event.map_url && (
                  <>
                    <span className="font-semibold text-muted-foreground block mb-1 mt-2">Map URL (pinned)</span>
                    <a href={event.map_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      Pinned listing <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                )}
                <span className="font-semibold text-muted-foreground block mb-1 mt-2">Discovery</span>
                <p>{event.discovery_source || "—"}</p>
              </div>
            </div>
            <EventRevisionInput eventId={event.id} onRevised={(changedFields) => {
              onEventRevised(event.id, changedFields);
            }} />
          </td>
        </tr>
      )}
    </>
  );
}
